import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNoticeToast } from '../lib/toast';
import { useFitHeight } from '../lib/fitHeight';
import { TopScrollbar } from '../components/TopScrollbar';
import {
  api, type AcctCorp, type CashBookInput, type CashBookRow, type CashBulkDeductRow, type CashRulePlanItem, type CashRuleRow,
} from '../lib/api';
import { ErrorBox, dash, money, MoneyInput } from '../components/DataTable';
import Modal from '../components/Modal';
import { AllocPicker } from './FinInst';
import { canScreen, useMe } from '../lib/me';
import { parseCashPaste } from '../lib/cashPaste';
import { useSticky, useAcctCorp } from '../lib/sticky';
import { useLocation } from 'react-router-dom';
import { urlParam } from '../lib/deepLink';
import { usePageActive } from '../lib/pageActive';
import OpsCashBadge, { hasOpsRef } from '../components/OpsCashBadge';
import { armCopy } from '../lib/cellCopy';
import { shiftRuleSummary } from '../lib/ruleSummary';

/**
 * 회계 1단계 C — 출납일보(#19)·매월 반복 출금. 기획 정본 `plan/accounting.md` 5-C.
 *
 * 시트(「회계팀 출납일보_2026」)의 **색 규칙을 그대로** 쓴다 — 회계팀이 눈으로 익힌 표시라서:
 *   글씨 파랑 = 버스인 공제 대상, 바탕 아이보리 = 공제 완료.
 * 버스인웹 재무추가에 바로 공제·일자별 대조 = 「재무추가 공제·대조」 창(#19 3번). 🔴 넣기 스위치는 버스인웹 전환 뒤에 연다(대조는 늘 본다).
 */

const WD = ['일', '월', '화', '수', '목', '금', '토'];
const kst = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
const monthOf = (d: string) => d.slice(0, 7);
const nextMonth = (m: string) => {
  const [y, mm] = m.split('-').map(Number) as [number, number];
  return mm === 12 ? `${y + 1}-01` : `${y}-${String(mm + 1).padStart(2, '0')}`;
};
const addMonths = (m: string, n: number) => {
  const [y, mm] = m.split('-').map(Number) as [number, number];
  const t = y * 12 + (mm - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
};
/** 지급일의 기본 정산월 — 6일~다음 달 5일 = 그 달(1~5일은 전월). 서버 `cashbookDeduct.ts defaultDeductMon` 과 같은 식(#57). */
const defaultDeductMon = (payDate: string) => (Number(payDate.slice(8, 10)) > 5 ? payDate.slice(0, 7) : addMonths(payDate.slice(0, 7), -1));
/** 자동 정산적용월 — 기본 정산월이 마감됐으면 마감 안 된 다음 달로. */
const autoDeductMon = (payDate: string, finished: readonly string[]) => {
  let m = defaultDeductMon(payDate);
  for (let i = 0; i < 24 && finished.includes(m); i++) m = nextMonth(m);
  return m;
};
const monLabel = (m: string) => (m === '' ? '정산월 없음' : `${m.slice(0, 4)}년 ${Number(m.slice(5, 7))}월`);
const dayLabel = (d: string) => `${d} (${WD[new Date(`${d}T00:00:00`).getDay()]})`;
// 구분에 「자동」이 든 줄(자동이체)은 구분 칸만 연한 초록, 「현금」이 든 줄은 연한 보라 — 한눈에 가르기(#38)
const autoMethod = (m: string | null) =>
  m?.includes('자동') ? 'bg-green-100' : m?.includes('현금') ? 'bg-violet-100' : '';
/** 출납 줄 → 시트 모양 탭 구분 줄(일자 | 구분 | 시재계정 | 업체 | 적요 | 금액 | 소계 | 증빙 | memo) — 「복사」·고른 줄 복사 공용. */
function tsvLines(rows: CashBookRow[]): string[] {
  const esc = (v: string | null) => (v ?? '').replace(/[\t\r\n]+/g, ' ');
  return rows.map((r) => [r.pay_date, esc(r.method), esc(r.cash_account), esc(r.vendor), esc(r.summary), String(Math.round(Number(r.amount))), '',
    r.evidence === null ? '' : r.evidence ? 'O' : 'X', esc(r.memo)].join('\t'));
}
/** 입력칸 금액 → 숫자 문자열(콤마 제거). 화면 계산은 표시용일 뿐 — 서버가 BigInt 로 다시 낸다. */
const clean = (v: string) => v.replace(/[,\s원]/g, '');
const supply = (v: string) => {
  const n = Number(clean(v));
  return Number.isFinite(n) && n > 0 ? String(Math.round(n / 1.1)) : '';
};
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

function CorpSelect({ corps, value, onChange, all = true }: {
  corps: AcctCorp[]; value: number | null; onChange: (v: number | null) => void; all?: boolean;
}) {
  return (
    <select className="field w-40" value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
      {all && <option value="">전체 법인</option>}
      {corps.map((c) => <option key={c.corp_id} value={c.corp_id}>{c.parent_corp_id ? `└ ${c.short_name}` : c.short_name}</option>)}
    </select>
  );
}

function useHints(corpId: number | null) {
  const [h, setH] = useState<{ methods: string[]; vendors: string[]; cashAccounts: string[]; deductAccounts: string[]; finishedMons: string[] }>(
    { methods: [], vendors: [], cashAccounts: [], deductAccounts: [], finishedMons: [] });
  useEffect(() => {
    api.listCashBookHints({ corpId }).then(setH).catch(() => undefined);
  }, [corpId]);
  return h;
}

// ─────────────────────────────────────────────────────────────────────────────
// 출납일보
// ─────────────────────────────────────────────────────────────────────────────
export function CashBookTab({ corps }: { corps: AcctCorp[] }) {
  const today = kst();
  // 기본 기간 = 어제~오늘(2026-09-29 게시판 #42 — 예전엔 이번 달 1일~말일).
  const [from, setFrom] = useSticky('acct.cash.from', new Date(Date.parse(`${today}T00:00:00Z`) - 86400_000).toISOString().slice(0, 10));
  const [to, setTo] = useSticky('acct.cash.to', today);
  const [corpId, setCorpId] = useAcctCorp();
  const [deduct, setDeduct] = useSticky<'all' | 'deduct' | 'pending'>('acct.cash.deduct', 'all');
  const [term, setTerm] = useSticky('acct.cash.term', '');
  const [q, setQ] = useSticky('acct.cash.q', '');
  const [nonce, setNonce] = useState(0);
  // 다른 화면에서 건너온 줄(`?cash=<id>&date=YYYY-MM-DD` · lib/deepLink.ts — 영업정산·OPS 탭·정산현황).
  // 그 날 하루로 맞추고 검색·공제 필터를 풀어 그 줄이 반드시 보이게 한다. 법인이 걸려 있어 안 보이면 「전체」로 한 번 푼다.
  const { search } = useLocation();
  const focusCash = urlParam(search, 'cash');
  const focusDate = urlParam(search, 'date');
  useEffect(() => {
    if (!focusCash) return;
    if (focusDate && /^\d{4}-\d{2}-\d{2}$/.test(focusDate)) { setFrom(focusDate); setTo(focusDate); }
    setTerm(''); setQ(''); setDeduct('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusCash, focusDate]);
  const [data, setData] = useState<Awaited<ReturnType<typeof api.listCashBook>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<CashBookRow | 'new' | null>(null);
  const [deductPanel, setDeductPanel] = useState(false);
  // #460 고른 줄 한 번에 공제하기 창
  const [bulkDeduct, setBulkDeduct] = useState<number[] | null>(null);
  const [pasting, setPasting] = useState(false);
  const { me } = useMe();
  const canWrite = canScreen(me, 'accounting', 'write');
  const [inline, setInline] = useState(false);
  // 칸 하나 저장 뒤 서버가 돌려준 줄로 그 줄만 바꾼다 — 목록을 다시 받으면 입력 중인 다음 칸의 포커스가 날아간다.
  const patched = useCallback((row: CashBookRow) =>
    setData((d) => (d ? { ...d, rows: d.rows.map((r) => (r.cash_book_id === row.cash_book_id ? row : r)) } : d)), []);
  // #459 B/ 계정 재무추가 미공제 — 검색 옆 버튼에 건수 · 남아 있으면 세션에 한 번 창을 띄운다(알림).
  const [bPending, setBPending] = useState<{ rows: CashBookRow[]; skipped: CashBookRow[]; from: string } | null>(null);
  const [bOpen, setBOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.listCashBookBPending({ corpId }).then((r) => {
      if (cancelled) return;
      setBPending(r);
      const key = `acct.cash.bpending-shown.${corpId ?? 'all'}`;
      try {
        if (r.rows.length > 0 && canWrite && !sessionStorage.getItem(key)) { sessionStorage.setItem(key, '1'); setBOpen(true); }
      } catch { /* 저장소 막힘 — 알림 창은 버튼으로 */ }
    }).catch(() => { /* 버튼만 안 보인다 — 목록 오류는 위 목록이 알린다 */ });
    return () => { cancelled = true; };
  }, [corpId, nonce, canWrite]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    api.listCashBook({ dateFrom: from, dateTo: to, corpId, deduct, q: q || undefined })
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to, corpId, deduct, q, nonce]);

  const days = useMemo(() => {
    const out: { date: string; rows: CashBookRow[]; sum: number }[] = [];
    for (const r of data?.rows ?? []) {
      const last = out[out.length - 1];
      if (last && last.date === r.pay_date) { last.rows.push(r); last.sum += Number(r.amount); }
      else out.push({ date: r.pay_date, rows: [r], sum: Number(r.amount) });
    }
    return out;
  }, [data]);
  /**
   * 표 줄은 **줄이 바뀔 때만** 다시 그린다(2026-09-30 속도 4차 — logPerf 09-29: 회계 멈춤 85건 · 합 48.8초 · 최대 2.7초).
   * 검색칸·날짜칸에 한 글자 칠 때마다 이 화면이 다시 그려지는데, 줄까지 따라 그리면 수천 줄 × 칸마다 한 번씩이라
   * 느린 PC 에서 글자 하나에 2초가 걸렸다. 여기 의존값에 매 렌더 새로 만드는 값(인라인 함수·객체)을 넣지 말 것.
   */
  /**
   * 여러 줄 고르기(#38 — 「드래그한 내역만 복붙·삭제·다른 날짜로」·「한 건씩 눌러 원하는 만큼 삭제」).
   * 맨 앞 네모를 누르면 바로 고르고, 줄 어디서든 **끌면** 여러 줄이 골라진다(그냥 누르면 칸 복사 · 두 번 누르면 창).
   * Shift = 앞서 고른 줄부터 여기까지 · Ctrl = 하나씩 더하기/빼기. 고른 뒤 Ctrl+C 복사 · Delete 삭제 · Esc 풀기.
   * 🔴 손잡이는 ref 로만 상태를 읽는다 — 안정된 객체여야 3,000줄 표가 고를 때마다 통째로 다시 그려지지 않는다(속도 4차).
   */
  const [sel, setSel] = useState<Set<number>>(() => new Set());
  const [dragging, setDragging] = useState(false);
  const order = useRef<number[]>([]);
  order.current = useMemo(() => (data?.rows ?? []).map((r) => r.cash_book_id), [data]);
  const drag = useRef<{ anchor: number | null; mode: 'none' | 'pending' | 'on'; base: Set<number>; justDragged: boolean }>(
    { anchor: null, mode: 'none', base: new Set(), justDragged: false });
  const selRef = useRef(sel);
  selRef.current = sel;
  const range = (a: number, b: number) => {
    const ids = order.current;
    let i = ids.indexOf(a); let j = ids.indexOf(b);
    if (i < 0 || j < 0) return [b];
    if (i > j) [i, j] = [j, i];
    return ids.slice(i, j + 1);
  };
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef(0);
  const say = useCallback((text: string) => {
    setToast(text);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2500);
  }, []);
  // 바로 입력 줄을 연 줄 번호(문자열 — bigint id). 저장하면 방금 넣은 줄로 옮겨 이어 입력한다.
  const [insertAt, setInsertAt] = useState<string | null>(null);
  // #376 끌어 옮기는 중인 줄 — ref 로만(끄는 동안 표를 다시 그리지 않는다 · 자리 표시는 줄 그림자를 직접 칠한다).
  const mv = useRef<{ ids: number[]; corp: number | null; mark: HTMLElement | null; after: boolean } | null>(null);
  const rowsRef = useRef<CashBookRow[] | undefined>(undefined);
  rowsRef.current = data?.rows;
  const act = useMemo<RowActions>(() => ({
    down: (id, e, grip) => {
      if (e.button !== 0) return;
      const t = e.target as HTMLElement;
      if (!grip && t.closest('input, button, select, a, textarea')) return;
      const d = drag.current;
      d.justDragged = false;
      const cur = selRef.current;
      if (e.shiftKey && d.anchor != null) {
        // 앞서 고른 줄부터 여기까지 — 줄을 눌러도 창이 안 열리게 삼킨다.
        setSel(new Set([...(e.ctrlKey || e.metaKey ? cur : []), ...range(d.anchor, id)]));
        d.justDragged = !grip; e.preventDefault();
        return;
      }
      if (e.ctrlKey || e.metaKey) {
        const next = new Set(cur);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSel(next); d.anchor = id; d.justDragged = !grip; e.preventDefault();
        return;
      }
      d.anchor = id;
      d.base = new Set();
      if (grip) {
        // 네모: 누를 때마다 그 줄만 더하거나 뺀다(#111 「건건이 누를 수 있도록」). 더한 뒤 누른 채 끌면 이어서 고른다.
        e.preventDefault();
        if (cur.has(id)) { const next = new Set(cur); next.delete(id); setSel(next); d.mode = 'none'; return; }
        d.base = new Set(cur);
        setSel(new Set([...cur, id]));
        d.mode = 'on'; setDragging(true);
      } else d.mode = 'pending';
    },
    enter: (id) => {
      const d = drag.current;
      if (d.mode === 'none' || d.anchor == null) return;
      if (d.mode === 'pending') {
        if (id === d.anchor) return;
        d.mode = 'on'; setDragging(true);
        window.getSelection()?.removeAllRanges();
      }
      d.justDragged = true;
      setSel(new Set([...d.base, ...range(d.anchor, id)]));
    },
    dragged: () => drag.current.justDragged,
    insert: (r) => setInsertAt(r ? String(r.cash_book_id) : null),
    inserted: (newId) => { setInsertAt(String(newId)); setNonce((n) => n + 1); },
    shift: (r, dir) => {
      api.shiftCashBookLine({ id: Number(r.cash_book_id), dir }).then(
        (x) => { if (x.moved) setNonce((n) => n + 1); else say(dir === 'up' ? '그 날 맨 위 줄입니다.' : '그 날 맨 아래 줄입니다.'); },
        (e) => say(errMsg(e)));
    },
    moveStart: (r, e) => {
      const cur = selRef.current;
      const ids = cur.has(r.cash_book_id) ? order.current.filter((x) => cur.has(x)) : [r.cash_book_id];
      const corps = new Set((rowsRef.current ?? []).filter((x) => ids.includes(x.cash_book_id)).map((x) => x.corp_id));
      mv.current = { ids, corp: corps.size === 1 ? [...corps][0] ?? null : null, mark: null, after: false };
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', `출납일보 ${ids.length}줄`);
    },
    moveOver: (r, e) => {
      const m = mv.current;
      if (!m || m.ids.includes(r.cash_book_id) || m.corp !== r.corp_id) return; // 놓을 수 없는 자리 = 기본 동작(금지 표시)
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      const tr = e.currentTarget as HTMLElement;
      const rc = tr.getBoundingClientRect();
      const after = e.clientY > rc.top + rc.height / 2;
      if (m.mark !== tr) { if (m.mark) m.mark.style.boxShadow = ''; m.mark = tr; }
      m.after = after;
      tr.style.boxShadow = after ? 'inset 0 -3px 0 #0284c7' : 'inset 0 3px 0 #0284c7';
    },
    moveLeave: (e) => {
      const m = mv.current;
      if (m?.mark && m.mark === e.currentTarget && !m.mark.contains(e.relatedTarget as Node | null)) { m.mark.style.boxShadow = ''; m.mark = null; }
    },
    moveDrop: (r, e) => {
      const m = mv.current;
      mv.current = null;
      if (m?.mark) m.mark.style.boxShadow = '';
      if (!m || m.ids.includes(r.cash_book_id) || m.corp !== r.corp_id) return;
      e.preventDefault();
      api.placeCashBookLines({ ids: m.ids, targetId: Number(r.cash_book_id), after: m.after }).then(
        (x) => { say(`${x.n}줄을 옮겼습니다.${x.dateMoved ? ` 그중 ${x.dateMoved}줄은 날짜도 ${r.pay_date} 로 바뀌었습니다.` : ''}`); setNonce((n) => n + 1); },
        (err) => say(errMsg(err)));
    },
    moveEnd: () => { const m = mv.current; if (m?.mark) m.mark.style.boxShadow = ''; mv.current = null; },
    copyCell: (label, value, el) => {
      if (!value) { say(`${label}${(label.charCodeAt(label.length - 1) - 0xac00) % 28 ? '이' : '가'} 비어 있습니다.`); return; }
      // #250 — 누르면 고르기만, Ctrl+C 로 복사.
      armCopy(value, el, (r) => say(r.ok
        ? `${label} 복사: ${value.length > 40 ? `${value.slice(0, 40)}…` : value}`
        : `복사하지 못했습니다: ${r.error}`));
    },
  }), [say]);
  useEffect(() => {
    const up = () => {
      const d = drag.current;
      if (d.mode !== 'none') { d.mode = 'none'; setDragging(false); }
      // 끌기가 끝난 뒤 따라오는 click 한 번만 삼키고 풀어 준다.
      window.setTimeout(() => { d.justDragged = false; }, 0);
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);
  // 목록이 바뀌면 사라진 줄은 선택에서 뺀다.
  useEffect(() => {
    setSel((cur) => {
      if (cur.size === 0) return cur;
      const live = new Set(order.current);
      const next = new Set([...cur].filter((id) => live.has(id)));
      return next.size === cur.size ? cur : next;
    });
  }, [data]);
  // 입력 줄을 연 줄의 날짜 — 그 날만 다시 그린다(다른 날은 insertAt '' 그대로라 memo 가 막는다).
  const insertDay = useMemo(
    () => (insertAt ? data?.rows.find((r) => String(r.cash_book_id) === insertAt)?.pay_date ?? null : null), [insertAt, data]);
  const selByDay = useMemo(() => {
    const m = new Map<string, number[]>();
    if (sel.size) for (const r of data?.rows ?? []) if (sel.has(r.cash_book_id)) m.set(r.pay_date, [...(m.get(r.pay_date) ?? []), r.cash_book_id]);
    return m;
  }, [sel, data]);

  const body = useMemo(() => days.map((d) => (
    <DayRows key={d.date} date={d.date} rows={d.rows} sum={d.sum} onEdit={setEditing}
             inline={inline} onPatched={patched} canWrite={canWrite} sel={selByDay.get(d.date)?.join(',') ?? ''} act={act}
             insertAt={insertDay === d.date ? insertAt! : ''}
             focus={focusCash && d.rows.some((r) => String(r.cash_book_id) === focusCash) ? focusCash : ''} />
  )), [days, inline, patched, canWrite, selByDay, act, insertAt, insertDay, focusCash]);

  /**
   * 표를 **화면 높이 안에** 묶고 머리글을 붙인다(2026-09-26 사용자 요청 — 내려도 칸 이름이 보이게).
   * `DataTable` 과 같은 방식: 박스 위치를 문서 기준으로 재서 남은 높이만큼 준다.
   * 🔴 sticky 머리글 배경은 `thead` 가 아니라 **셀마다**(issues 115 — row-group 배경은 sticky 에서 안 칠해진다).
   */
  const boxRef = useRef<HTMLDivElement>(null);
  const boxH = useFitHeight(boxRef, [data, error], { min: '20rem' });

  // 끌어 고르는 중 마우스가 표 위·아래 끝에 닿으면 표를 굴리고, 굴린 자리의 줄까지 이어서 고른다
  // (10-15 #288 이현숙 「보이는 화면까지만 선택되고 아래로 안 내려간다」).
  useEffect(() => {
    if (!dragging) return;
    let x = 0; let y = 0; let last: string | null = null;
    const move = (e: MouseEvent) => { x = e.clientX; y = e.clientY; };
    window.addEventListener('mousemove', move);
    const timer = window.setInterval(() => {
      const box = boxRef.current;
      if (!box || (!x && !y)) return;
      const rc = box.getBoundingClientRect();
      const head = (box.querySelector('thead') as HTMLElement | null)?.offsetHeight ?? 0;
      const top = rc.top + head; const edge = 40;
      let dy = 0;
      if (y > rc.bottom - edge) dy = Math.min(60, (y - (rc.bottom - edge)) / 2 + 6);
      else if (y < top + edge) dy = -Math.min(60, ((top + edge) - y) / 2 + 6);
      if (!dy) return;
      const before = box.scrollTop;
      box.scrollTop += dy;
      if (box.scrollTop === before) return;
      const px = Math.min(Math.max(x, rc.left + 4), rc.right - 4);
      const py = Math.min(Math.max(y, top + 2), rc.bottom - 4);
      const tr = document.elementFromPoint(px, py)?.closest('tr[data-cash-id]');
      const key = tr?.getAttribute('data-cash-id');
      if (!key || key === last) return;
      last = key;
      const id = order.current.find((v) => String(v) === key);
      if (id != null) act.enter(id);
    }, 30);
    return () => { window.removeEventListener('mousemove', move); window.clearInterval(timer); };
  }, [dragging, act]);

  // 조건을 바꿨을 때·바로 입력으로 저장했을 때만 목록 끝(최근 날짜 + 입력 줄)으로 내린다. 창에서 고친 뒤엔 보던 자리를 지킨다.
  const scrollEnd = useRef(true);
  useEffect(() => { scrollEnd.current = true; }, [from, to, corpId, deduct, q]);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || !data || !scrollEnd.current) return;
    scrollEnd.current = false;
    el.scrollTop = el.scrollHeight;
  }, [data]);
  const focusDone = useRef('');
  // 링크로 온 줄: 목록에 있으면 그 줄로 스크롤(위 «끝으로» 뒤에 돈다) · 없고 법인이 걸려 있으면 법인을 풀어 다시 받는다.
  useEffect(() => {
    if (!focusCash || !data || loading || focusDone.current === search) return;
    if (!data.rows.some((r) => String(r.cash_book_id) === focusCash)) {
      if (corpId !== null) setCorpId(null); else focusDone.current = search;
      return;
    }
    focusDone.current = search; // 한 번만 — 그 뒤 사람이 법인·날짜를 바꿔도 다시 끌고 오지 않는다.
    const id = window.setTimeout(() => boxRef.current?.querySelector(`[data-cash-id="${focusCash}"]`)?.scrollIntoView({ block: 'center' }), 50);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, focusCash, loading]);

  const [copied, setCopied] = useState<string | null>(null);
  /** 보이는 줄을 시트 모양(일자 | 구분 | 시재계정 | 업체 | 적요 | 금액 | 소계 | 증빙 | memo)으로 복사 — 엑셀·더존에 그대로 붙는다(#38·#37 복사). */
  async function copyRows() {
    const lines = ['일자 (요일)\t구분(인출계좌/계정)\t시재계정\t업체(기사)\t적요\t금액\t소계\t매입(증빙)여부\tmemo', ...tsvLines(data?.rows ?? [])];
    try { await navigator.clipboard.writeText(lines.join('\n')); setCopied(`${lines.length - 1}줄을 복사했습니다 — 엑셀에 붙여넣으세요.`); }
    catch (e) { setCopied(`복사하지 못했습니다: ${errMsg(e)}`); }
    window.setTimeout(() => setCopied(null), 4000);
  }

  // ─── 고른 줄로 하는 일(#38) ───
  const picked = useMemo(() => (data?.rows ?? []).filter((r) => sel.has(r.cash_book_id)), [data, sel]);
  const [delArm, setDelArm] = useState(false);
  const [moveTo, setMoveTo] = useState('');
  const [selBusy, setSelBusy] = useState(false);
  useEffect(() => { setDelArm(false); }, [sel]);
  /** 고른 줄을 머리글 없이 복사 — 엑셀에도, 이 화면 「붙여넣기」에도 그대로 붙는다(칸 순서가 같다). */
  const copySel = useCallback(async () => {
    try { await navigator.clipboard.writeText(tsvLines(picked).join('\n')); say(`${picked.length}줄을 복사했습니다 — 엑셀이나 「붙여넣기」에 붙이세요.`); }
    catch (e) { say(`복사하지 못했습니다: ${errMsg(e)}`); }
  }, [picked, say]);
  const delSel = useCallback(async () => {
    if (!delArm) { setDelArm(true); return; }
    setSelBusy(true);
    try {
      const r = await api.deleteCashBooks({ ids: picked.map((x) => x.cash_book_id) });
      say(`${r.deleted}줄을 지웠습니다.${r.removed ? ` 재무추가에 넣었던 공제 ${r.removed}건도 같이 지웠습니다.` : ''}`);
      setSel(new Set()); setNonce((n) => n + 1);
    } catch (e) { say(errMsg(e)); } finally { setSelBusy(false); setDelArm(false); }
  }, [delArm, picked, say]);
  const moveSel = useCallback(async (copy: boolean) => {
    if (!moveTo) return;
    setSelBusy(true);
    try {
      const r = await api.moveCashBooks({ ids: picked.map((x) => x.cash_book_id), payDate: moveTo, copy });
      say(`${r.n}줄을 ${moveTo} 로 ${copy ? '복사했습니다(공제 표시는 빼고)' : '옮겼습니다'}.${moveTo < from || moveTo > to ? ' 지금 기간 밖이라 목록에는 안 보입니다.' : ''}`);
      setSel(new Set()); setNonce((n) => n + 1);
    } catch (e) { say(errMsg(e)); } finally { setSelBusy(false); }
  }, [moveTo, picked, say, from, to]);
  // #113 — 고른 줄 적요 바꾸기(시트 Ctrl+H). 글자 그대로 찾는다. 바꾼 뒤에도 고름은 남겨 둔다(이어서 한 번 더 바꿀 수 있게).
  const [findTxt, setFindTxt] = useState('');
  const [toTxt, setToTxt] = useState('');
  const hitN = useMemo(() => (findTxt ? picked.filter((r) => (r.summary ?? '').includes(findTxt)).length : 0), [picked, findTxt]);
  const replaceSel = useCallback(async () => {
    if (!findTxt || hitN === 0) return;
    setSelBusy(true);
    try {
      const r = await api.replaceCashBookSummary({ ids: picked.map((x) => x.cash_book_id), find: findTxt, to: toTxt });
      say(`${r.n}줄의 적요에서 「${findTxt}」를 「${toTxt}」(으)로 바꿨습니다.`);
      setFindTxt(''); setToTxt(''); setNonce((n) => n + 1);
    } catch (e) { say(errMsg(e)); } finally { setSelBusy(false); }
  }, [findTxt, toTxt, hitN, picked, say]);
  // 키보드 — 칸에 입력 중이거나 창이 떠 있으면 건드리지 않는다.
  // 화면 탭(#168)으로 숨어 있을 때도 손 뗀다 — 안 보이는 출납 줄을 Delete 로 지우면 안 된다.
  const pageActive = usePageActive();
  const modalOpen = !pageActive || !!(editing || pasting || deductPanel || bulkDeduct);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (modalOpen || selRef.current.size === 0) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.closest('input, textarea, select') || t.isContentEditable)) return;
      if (e.key === 'Escape') { setSel(new Set()); return; }
      // 눌러 고른 칸이 있으면 그 칸 복사가 먼저(#250 · lib/cellCopy 가 이미 처리했다).
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c' && !window.getSelection()?.toString()) {
        if (!e.defaultPrevented) { e.preventDefault(); void copySel(); }
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && canWrite && !selBusy) { e.preventDefault(); void delSel(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen, copySel, delSel, canWrite, selBusy]);
  const selSum = picked.reduce((s, r) => s + Number(r.amount), 0);

  const grand = data?.totals.find((t) => t.corp_name === null);
  const th = 'sticky top-0 z-10 bg-surface px-2 py-1.5 font-medium';
  return (
    <div className="space-y-2">
      <div className="btn-row-sm flex flex-wrap items-center gap-2">
        <input type="date" className="field w-40" value={from} onChange={(e) => setFrom(e.target.value)} />
        <span className="text-ink-faint">~</span>
        <input type="date" className="field w-40" value={to} onChange={(e) => setTo(e.target.value)} />
        <CorpSelect corps={corps} value={corpId} onChange={setCorpId} />
        <select className="field w-36" value={deduct} onChange={(e) => setDeduct(e.target.value as typeof deduct)}>
          <option value="all">전체</option>
          <option value="deduct">버스인 공제 대상</option>
          <option value="pending">공제 안 끝난 것</option>
        </select>
        <form onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); }} className="flex items-center gap-2">
          <input className="field w-56" placeholder="업체·적요·구분·금액" value={term} onChange={(e) => setTerm(e.target.value)} />
          <button className="btn-search" type="submit" aria-label="검색" title="검색" />
        </form>
        {bPending && (
          <button className={bPending.rows.length ? 'btn border-amber-400 bg-amber-50 text-amber-800' : 'btn'} onClick={() => setBOpen(true)}
                  title="구분이 B/ 로 시작하는데 재무추가 공제가 안 된 줄(범칙금·통행료 제외)">
            미공제 B/ 계정{bPending.rows.length ? ` ${bPending.rows.length}` : ''}
          </button>
        )}
        <span className="ml-auto text-xs text-ink-faint" title="시트와 같은 표시입니다. 줄을 두 번 누르면 고칩니다.">
          <span className="font-medium text-blue-700">파랑 글씨 = 공제 대상</span>
          <span className="ml-1 rounded bg-amber-50 px-1">아이보리 = 공제 완료</span>
        </span>
        <button className="btn" onClick={() => setDeductPanel(true)} title="공제 줄을 버스인웹 재무추가에 넣고, 날짜별로 출납일보 공제 합계와 재무추가 공제 합계를 맞춰 봅니다">재무추가 공제·대조</button>
        {canWrite && (
          <button className={inline ? 'btn border-brand-500 bg-brand-50 text-brand-700' : 'btn'} onClick={() => setInline((x) => !x)}
                  title="표의 칸을 바로 고칩니다. 칸을 벗어나거나 Enter 를 누르면 그 칸이 저장됩니다.">
            {inline ? '표에서 고치기 끝' : '표에서 고치기'}
          </button>
        )}
        <button className="btn" onClick={() => void copyRows()} disabled={!data?.rows.length} title="보이는 줄을 복사합니다 — 엑셀·더존에 바로 붙여넣을 수 있습니다">복사</button>
        <button className="btn" onClick={() => setPasting(true)} title="시트·엑셀에서 여러 줄을 복사해 넣거나, 양식 파일로 한 번에 올립니다">붙여넣기·엑셀 올리기</button>
        <button className="btn-primary" onClick={() => setEditing('new')}>줄 추가</button>
      </div>
      {copied && <p className="text-xs text-emerald-700">{copied}</p>}
      {/* 고름 막대와 안내 문구는 같은 높이 칸에 — 높이가 달라 표가 밀리면 네모를 누른 마우스 밑에 다음 줄이 와 두 줄이 골라졌다(#111). */}
      <div className="flex h-12 items-center">
      {sel.size > 0 ? (
        <div className="flex h-full w-full flex-nowrap items-center gap-2 overflow-x-auto whitespace-nowrap rounded-lg border border-sky-300 bg-sky-50 px-3 text-sm">
          <b>{sel.size.toLocaleString()}줄 고름</b>
          <span className="tabular-nums">합계 {money(selSum)}</span>
          {/* #460 맨 앞 — 막대 끝(약 1,400px 뒤)에 두면 노트북 폭에선 가로 스크롤 밖이라 배포 뒤 3일간 0회 사용(11-02) */}
          {canWrite && (
            <button className="btn border-blue-400 bg-blue-50 py-0.5 font-medium text-blue-700" disabled={selBusy}
                    onClick={() => setBulkDeduct(picked.map((x) => Number(x.cash_book_id)))}
                    title="고른 줄을 창 하나에서 편성·계정·정산적용월을 정해 한 번에 버스인 재무추가에 공제합니다">
              한 번에 공제하기
            </button>
          )}
          <button className="btn py-0.5" onClick={() => void copySel()} title="고른 줄을 복사합니다 — 엑셀이나 「붙여넣기」에 붙이세요">복사 (Ctrl+C)</button>
          {canWrite && (
            <>
              <button className={`btn py-0.5 ${delArm ? 'border-red-500 bg-red-50 text-red-700' : 'text-red-600'}`} disabled={selBusy}
                      onClick={() => void delSel()} onBlur={() => setDelArm(false)}>
                {delArm ? `${sel.size}줄을 지웁니다 — 한 번 더` : '삭제 (Delete)'}
              </button>
              <span className="ml-2 text-ink-soft">다른 날짜로</span>
              <input type="date" className="field h-8 w-40 py-0" value={moveTo} onChange={(e) => setMoveTo(e.target.value)} />
              <button className="btn py-0.5" disabled={!moveTo || selBusy} onClick={() => void moveSel(false)}>옮기기</button>
              <button className="btn py-0.5" disabled={!moveTo || selBusy} onClick={() => void moveSel(true)}
                      title="고른 줄을 그 날짜에 새 줄로 넣습니다. 공제 표시는 따라가지 않습니다.">복사해 넣기</button>
              <span className="ml-2 text-ink-soft">적요</span>
              <input className="field h-8 w-32 py-0" placeholder="찾을 글자" value={findTxt} onChange={(e) => setFindTxt(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') void replaceSel(); }} />
              <span className="text-ink-faint">→</span>
              <input className="field h-8 w-32 py-0" placeholder="바꿀 글자" value={toTxt} onChange={(e) => setToTxt(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') void replaceSel(); }} />
              <button className="btn py-0.5" disabled={!findTxt || hitN === 0 || selBusy} onClick={() => void replaceSel()}
                      title="고른 줄의 적요에서 찾을 글자를 모두 바꿉니다(엑셀 Ctrl+H 처럼). 그 글자가 없는 줄은 그대로 둡니다.">
                {findTxt ? `바꾸기 (${hitN}줄)` : '바꾸기'}
              </button>
            </>
          )}
          <button className="btn-ghost ml-auto py-0.5" onClick={() => setSel(new Set())}>선택 풀기 (Esc)</button>
        </div>
      ) : (
        <p className="line-clamp-2 text-xs text-ink-faint">
          줄을 끌거나 맨 앞 네모를 하나씩 누르면 여러 줄을 고릅니다(Shift 로 이어서) — 복사·삭제·다른 날짜로 옮기기·적요 바꾸기·한 번에 공제하기.
          {' '}줄을 두 번 누르면 고치는 창이 뜹니다. 적요·금액·memo 칸은 눌러서 고른 뒤 Ctrl+C 를 누르면 그 칸만 복사됩니다. 줄에 마우스를 올리면 나오는 ＋ = 그 아래에 한 줄 바로 입력 · ▲▼ = 순서 바꾸기.
        </p>
      )}
      </div>
      {error && <ErrorBox message={error} />}
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        {data?.totals.filter((t) => t.corp_name !== null).map((t) => (
          <span key={t.corp_name} className="rounded bg-canvas px-2 py-0.5">
            {t.corp_name} {t.n}건 · <b>{money(t.amount)}</b>
            {Number(t.deduct_amount) > 0 && <span className="text-blue-700"> · 공제 {money(t.deduct_amount)}</span>}
            {Number(t.pending) > 0 && <span className="text-amber-700"> (미완 {money(t.pending)})</span>}
          </span>
        ))}
        {grand && <span className="rounded bg-brand-50 px-2 py-0.5 font-semibold">합계 {grand.n}건 · {money(grand.amount)}</span>}
        {data && data.rows.length === 0 && !loading && <span className="text-ink-faint">이 조건의 출납이 없습니다.</span>}
        {data?.truncated && <span className="text-amber-700">3,000줄까지만 보입니다 — 기간을 줄여 주세요.</span>}
      </div>
      {/* 🔴 칸 안에서 줄바꿈하지 않는다(2026-09-26 사용자 요청) — 긴 값은 말줄임 + 툴팁으로 전체를 본다. */}
      <TopScrollbar target={boxRef} />
      <div ref={boxRef} className="overflow-auto rounded-lg border border-line" style={{ maxHeight: boxH }}>
        <table className={`w-full whitespace-nowrap text-sm ${dragging ? 'select-none' : ''}`}>
          <thead>
            <tr className="text-left text-ink-soft">
              <th className={`${th} w-20 px-1`} title="눌러 고르기 · 끌면 여러 줄" /><th className={th}>법인</th><th className={th}>구분(인출계좌/계정)</th><th className={th}>시재계정</th>
              <th className={th}>업체(기사)</th><th className={th}>적요</th><th className={`${th} text-right`}>금액</th>
              <th className={th}>증빙</th><th className={th}>memo</th><th className={`${th} text-right`}>공제액</th><th className={th}>공제</th>
            </tr>
          </thead>
          <tbody className={loading ? 'opacity-50' : ''}>{body}</tbody>
          {/* 바로 입력 줄은 표 **맨 아래**에 붙어 있다(#38 4번 — 시트처럼 아래로 한 줄씩). 저장하면 목록 끝으로 내려가 방금 줄이 입력 줄 바로 위에 보인다. */}
          {canWrite && (
            <tfoot>
              <QuickEntryRow corps={corps} defaultCorp={corpId ?? corps.find((c) => !c.parent_corp_id)?.corp_id ?? 0}
                             defaultDate={today >= from && today <= to ? today : from}
                             onSaved={() => { scrollEnd.current = true; setNonce((n) => n + 1); }} />
            </tfoot>
          )}
        </table>
      </div>
      {bulkDeduct && (
        <Modal width="max-w-7xl">
          <BulkDeductPanel ids={bulkDeduct} onClose={() => setBulkDeduct(null)}
                           onDone={(msg) => { setBulkDeduct(null); setSel(new Set()); say(msg); setNonce((n) => n + 1); }} />
        </Modal>
      )}
      {deductPanel && (
        <Modal width="max-w-4xl">
          <DeductCheckPanel from={from} to={to} corpId={corpId} onClose={() => setDeductPanel(false)} onChanged={() => setNonce((n) => n + 1)} />
        </Modal>
      )}
      {bOpen && bPending && (
        <Modal width="max-w-6xl">
          <BPendingPanel corpId={corpId} rows={bPending.rows} skipped={bPending.skipped ?? []} from={bPending.from} canWrite={canWrite}
                         onClose={() => setBOpen(false)} onOpen={(r) => { setBOpen(false); setEditing(r); }}
                         onChanged={() => setNonce((n) => n + 1)} />
        </Modal>
      )}
      {pasting && (
        <Modal width="max-w-5xl">
          <PasteForm corps={corps} defaultCorp={corpId ?? corps.find((c) => !c.parent_corp_id)?.corp_id ?? 0}
                     defaultDate={today >= from && today <= to ? today : from}
                     onClose={() => setPasting(false)} onSaved={() => setNonce((n) => n + 1)} />
        </Modal>
      )}
      {editing && (
        <Modal width="max-w-2xl">
          <CashBookForm corps={corps} row={editing === 'new' ? null : editing}
                        defaultCorp={corpId ?? corps.find((c) => !c.parent_corp_id)?.corp_id ?? 0}
                        defaultDate={today >= from && today <= to ? today : from}
                        onClose={() => setEditing(null)} onSaved={() => setNonce((n) => n + 1)} />
        </Modal>
      )}
      {toast && (
        <div className="pointer-events-none fixed bottom-6 left-1/2 z-50 max-w-[90vw] -translate-x-1/2 truncate rounded-lg bg-ink px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

/**
 * 바로 입력 줄 — 표 맨 위에서 시트처럼 칸을 옮겨 가며 타이핑하고 **Enter 로 저장**(2026-09-28 way 「리스트에 바로 타이핑」).
 * 저장하면 업체·적요·금액·증빙·memo 만 비우고 날짜·법인·구분·시재계정은 남긴다(한 날짜 아래 여러 줄 쓰던 흐름).
 * 공제 표시·분납·편성은 여기서 안 받는다 — 저장한 줄을 눌러 창에서 단다.
 */
function QuickEntryRow({ corps, defaultCorp, defaultDate, onSaved }: {
  corps: AcctCorp[]; defaultCorp: number; defaultDate: string; onSaved: () => void;
}) {
  const [date, setDate] = useState(defaultDate);
  const [corpId, setCorpId] = useState<number>(defaultCorp);
  const [v, setV] = useState({ method: '', cashAccount: '', vendor: '', summary: '', amount: '', evidence: false, memo: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const hints = useHints(corpId || null);
  // 입력 제안(최대 300 × 3) — 글자 칠 때마다 다시 그리지 않는다(속도 4차).
  const lists = useMemo(() => (
    <tr className="hidden">
      <td>
        <datalist id="qe-methods">{hints.methods.map((x) => <option key={x} value={x} />)}</datalist>
        <datalist id="qe-accounts">{hints.cashAccounts.map((x) => <option key={x} value={x} />)}</datalist>
        <datalist id="qe-vendors">{hints.vendors.map((x) => <option key={x} value={x} />)}</datalist>
      </td>
    </tr>
  ), [hints]);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { setCorpId(defaultCorp); }, [defaultCorp]);
  useEffect(() => { setDate(defaultDate); }, [defaultDate]);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => { setV((x) => ({ ...x, [k]: e.target.value })); setMsg(null); };

  async function save() {
    if (busy) return;
    if (!clean(v.amount)) { setMsg({ ok: false, text: '금액을 넣어 주세요.' }); return; }
    setBusy(true); setMsg(null);
    try {
      await api.saveCashBook({
        payDate: date, corpId, method: v.method, cashAccount: v.cashAccount, vendor: v.vendor, summary: v.summary,
        // 체크 = 증빙 있음(O). 안 켜면 예전처럼 비워 둔다(해당 없음) — X 는 목록에서 체크를 풀 때 붙는다.
        amount: clean(v.amount), evidence: v.evidence ? true : null, memo: v.memo,
      });
      setMsg({ ok: true, text: `저장 — ${v.vendor || v.summary || ''} ${Number(clean(v.amount)).toLocaleString()}원` });
      setV((x) => ({ ...x, vendor: '', summary: '', amount: '', evidence: false, memo: '' }));
      onSaved();
      first.current?.focus();
      first.current?.select();
    } catch (e) { setMsg({ ok: false, text: errMsg(e) }); } finally { setBusy(false); }
  }
  const onKey = (e: React.KeyboardEvent) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); void save(); } };
  const cell = 'field h-8 w-full px-1.5 py-0 text-sm';
  // 🔴 표 맨 아래에 붙인다(sticky) — 배경은 row 가 아니라 칸마다(issues 115). 불투명이어야 아래로 지나가는 줄이 비치지 않는다.
  const td = 'sticky bottom-0 z-10 border-t-2 border-brand-500 bg-brand-50 px-1 py-1';
  return (
    <>
      <tr onKeyDown={onKey}>
        <td className={td} />
        <td className={td}>
          <div className="flex gap-1">
            <input type="date" className={`${cell} w-[8.5rem]`} value={date} onChange={(e) => setDate(e.target.value)} title="일자" />
            <select className={`${cell} w-24`} value={corpId} onChange={(e) => setCorpId(Number(e.target.value))} title="법인">
              {corps.map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name}</option>)}
            </select>
          </div>
        </td>
        <td className={td}><input ref={first} className={cell} list="qe-methods" placeholder="구분" value={v.method} onChange={set('method')} /></td>
        <td className={td}><input className={cell} list="qe-accounts" placeholder="시재계정" value={v.cashAccount} onChange={set('cashAccount')} /></td>
        <td className={td}><input className={cell} list="qe-vendors" placeholder="업체(기사)" value={v.vendor} onChange={set('vendor')} /></td>
        <td className={td}><input className={`${cell} min-w-[26rem]`} placeholder="적요" value={v.summary} onChange={set('summary')} /></td>
        <td className={td}>
          <input className={`${cell} w-24 text-right`} inputMode="numeric" placeholder="금액" value={v.amount}
                 onChange={(e) => { const d = e.target.value.replace(/[^\d-]/g, ''); setV((x) => ({ ...x, amount: d && d !== '-' ? Number(d).toLocaleString() : d })); setMsg(null); }} />
        </td>
        <td className={`${td} text-center`}>
          <input type="checkbox" className="h-4 w-4 align-middle" checked={v.evidence} title="매입(증빙) 있음"
                 onChange={(e) => { setV((x) => ({ ...x, evidence: e.target.checked })); setMsg(null); }} />
        </td>
        <td className={td}><input className={cell} placeholder="memo" value={v.memo} onChange={set('memo')} /></td>
        <td className={td} colSpan={2}>
          <div className="flex items-center gap-2">
            <button type="button" className="btn-primary h-8 whitespace-nowrap px-3 py-0 text-xs" disabled={busy} onClick={() => void save()}>
              {busy ? '저장 중…' : '저장 (Enter)'}
            </button>
            {msg && <span className={`max-w-[14rem] truncate text-xs ${msg.ok ? 'text-emerald-700' : 'text-red-600'}`} title={msg.text}>{msg.text}</span>}
          </div>
        </td>
      </tr>
      {lists}
    </>
  );
}

type PatchField = 'method' | 'cashAccount' | 'vendor' | 'summary' | 'memo' | 'evidence' | 'amount';

/**
 * 표에서 칸 하나 고치기 — 칸을 벗어나거나 Enter 면 **그 칸만** 저장(`patchCashBook`), Esc 는 되돌리기.
 * 바뀐 게 없으면 안 부른다. 저장 중·저장됨·실패를 칸 테두리로 보여 준다(DYOPS 사용자 설정과 같은 느낌).
 */
function EditCell({ row, field, value, onPatched, numeric, width, list, disabledReason }: {
  row: CashBookRow; field: PatchField; value: string; onPatched: (r: CashBookRow) => void;
  numeric?: boolean; width: string; list?: string; disabledReason?: string;
}) {
  const [v, setV] = useState(value);
  const [state, setState] = useState<'idle' | 'saving' | 'ok' | 'err'>('idle');
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setV(value); }, [value]);
  async function commit() {
    const now = numeric ? clean(v) : v.trim();
    const before = numeric ? clean(value) : value.trim();
    if (now === before) return;
    setState('saving'); setErr(null);
    try {
      const r = await api.patchCashBook({ id: row.cash_book_id, field, value: now });
      onPatched(r.row);
      setState('ok');
      window.setTimeout(() => setState((s) => (s === 'ok' ? 'idle' : s)), 1500);
    } catch (e) { setState('err'); setErr(errMsg(e)); }
  }
  const ring = state === 'saving' ? 'border-amber-400' : state === 'ok' ? 'border-emerald-500 bg-emerald-50' : state === 'err' ? 'border-red-500 bg-red-50' : '';
  if (disabledReason) return <span className="block px-1.5 text-right" title={disabledReason}>{money(value)}</span>;
  return (
    <input
      className={`field h-7 px-1.5 py-0 text-sm ${width} ${numeric ? 'text-right' : ''} ${ring}`}
      value={numeric && /^-?\d+$/.test(v) ? Number(v).toLocaleString() : v}
      list={list}
      inputMode={numeric ? 'numeric' : undefined}
      title={err ?? undefined}
      onChange={(e) => { setV(numeric ? e.target.value.replace(/[^\d-]/g, '') : e.target.value); if (state === 'err') setState('idle'); }}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.nativeEvent.isComposing) (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') { setV(value); setState('idle'); }
      }}
    />
  );
}

/**
 * 증빙 체크박스(#38 2번 — 창을 열지 않고 목록에서 바로). 켜기 = O, 끄기 = X. 비어 있던 줄(해당 없음)도 꺼진 칸으로 보인다.
 * 줄을 두 번 누르면 창이 열리므로 클릭이 줄로 올라가지 않게 막는다. 입력 권한이 없으면 보기만.
 */
function EvidenceCheck({ row, onPatched, canWrite }: { row: CashBookRow; onPatched: (r: CashBookRow) => void; canWrite: boolean }) {
  const [state, setState] = useState<'idle' | 'saving' | 'err'>('idle');
  const tip = row.evidence === null ? '증빙: 해당 없음' : row.evidence ? '증빙 있음(O)' : '증빙 없음(X)';
  return (
    <span className={`inline-flex rounded p-0.5 ${state === 'err' ? 'ring-2 ring-red-500' : state === 'saving' ? 'ring-2 ring-amber-400' : ''}`}
          onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()} title={state === 'err' ? '저장하지 못했습니다 — 다시 눌러 주세요' : tip}>
      <input type="checkbox" className="h-4 w-4 cursor-pointer disabled:cursor-default" checked={row.evidence === true}
             disabled={!canWrite || state === 'saving'}
             onChange={async (e) => {
               setState('saving');
               try { onPatched((await api.patchCashBook({ id: row.cash_book_id, field: 'evidence', value: e.target.checked ? 'O' : 'X' })).row); setState('idle'); }
               catch { setState('err'); }
             }} />
    </span>
  );
}

/**
 * DYOPS 경조금 줄 꼬리표 + 「지급완료」(10-22 김윤진·이현숙). 누르면 이 줄의 일자·금액으로 DYOPS 경조금 대장이 지급 완료가 되고
 * 신청자에게 알림이 간다. 지급 여부의 정본은 DYOPS 대장 — DYOPS 에서 직접 지급한 건도 「지급완료」로 보인다(그 취소는 DYOPS 에서).
 */
function CondolenceBadge({ row, onPatched, canWrite }: { row: CashBookRow; onPatched: (r: CashBookRow) => void; canWrite: boolean }) {
  const c = row.condolence!;
  const [busy, setBusy] = useState(false);
  const label = c.mode === 'created' ? '경조금 자동' : '경조금 연결';
  const tip = c.mode === 'created'
    ? 'DYOPS 경조금 지급 요청일에서 자동으로 들어간 줄 — 요청일·지급액이 바뀌면 따라 옮겨집니다'
    : '먼저 손으로 넣은 줄이라 새로 만들지 않고 DYOPS 경조금에 연결했습니다';
  async function toggle(paid: boolean) {
    const msg = paid
      ? `이 줄(${row.pay_date} · ${money(row.amount)}원)로 경조금을 지급 완료 처리할까요?\nDYOPS 경조금 대장이 지급 완료가 되고 신청자에게 알림이 갑니다.`
      : '경조금 지급완료를 취소할까요? DYOPS 경조금 대장이 다시 지급 대기가 됩니다.';
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.setCondolencePaid({ cashBookId: row.cash_book_id, paid });
      onPatched({ ...row, condolence: { ...c, paid, paidDate: paid ? row.pay_date : null, via: paid ? 'businweb' : null } });
    } catch (e) {
      window.alert(e instanceof Error ? e.message : '처리하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <span className="inline-flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}>
      <span className={`rounded px-1 text-[10px] ${c.mode === 'created' ? 'bg-rose-100 font-bold text-rose-800' : 'bg-rose-50 text-rose-700'}`} title={tip}>{label}</span>
      {c.paid
        ? <button type="button" className="rounded bg-emerald-600 px-1.5 text-[10px] font-bold text-white disabled:opacity-60"
                  disabled={!canWrite || busy || c.via !== 'businweb'}
                  title={c.via === 'businweb' ? `지급완료${c.paidDate ? ` ${c.paidDate}` : ''} — 누르면 취소` : 'DYOPS 경조금 대장에서 지급 처리된 건입니다(취소도 DYOPS 에서)'}
                  onClick={() => void toggle(false)}>지급완료 ✓</button>
        : canWrite && <button type="button" className="rounded border border-emerald-600 px-1.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                              disabled={busy} title="지급한 뒤 누르면 DYOPS 경조금 대장이 지급 완료가 됩니다" onClick={() => void toggle(true)}>
            {busy ? '처리 중…' : '지급완료'}
          </button>}
    </span>
  );
}

/** 줄 고르기·복사 손잡이(#38) — 부모가 한 번 만들어 모든 날에 같은 객체를 준다(줄 다시 그리기를 막으려고 안정된 값). */
interface RowActions {
  /** 줄에서 마우스를 누름 — `grip` = 맨 앞 고르기 칸(바로 고른다), 아니면 끌어야 고른다(한 번 = 칸 복사 · 두 번 = 창). */
  down: (id: number, e: React.MouseEvent, grip: boolean) => void;
  enter: (id: number) => void;
  /** 방금 끌어서 골랐으면 true — 그 뒤 따라오는 클릭(창 열기·칸 복사)을 삼킨다. */
  dragged: () => boolean;
  /** 이 줄 아래에 바로 입력 줄을 연다 — null 이면 닫는다. */
  insert: (r: CashBookRow | null) => void;
  /** 바로 입력 줄에서 저장함 — 방금 넣은 줄 아래로 입력 줄을 옮기고 목록을 다시 받는다. */
  inserted: (newId: number) => void;
  shift: (r: CashBookRow, dir: 'up' | 'down') => void;
  copyCell: (label: string, value: string, el: HTMLElement) => void;
  /** #376 끌어 옮기기 — 손잡이에서 시작(고른 줄이면 고른 줄 전부) · 줄 위에서 위/아래 반쪽으로 자리 · 놓으면 서버가 순서를 매긴다. */
  moveStart: (r: CashBookRow, e: React.DragEvent) => void;
  moveOver: (r: CashBookRow, e: React.DragEvent) => void;
  moveLeave: (e: React.DragEvent) => void;
  moveDrop: (r: CashBookRow, e: React.DragEvent) => void;
  moveEnd: () => void;
}

/**
 * 줄 사이 바로 입력(#38 후속 — 「줄추가는 눌렀을 때 아래에 한 줄로 키인」). 고른 줄의 날짜·법인으로, 그 줄 바로 아래에 넣는다.
 * Enter = 저장하고 방금 넣은 줄 아래에 새 입력 줄 · Esc = 닫기. 공제 표시는 저장한 줄을 눌러 창에서 단다(아래 바로 입력 줄과 같다).
 */
function InsertRow({ after, act }: { after: CashBookRow; act: RowActions }) {
  const [v, setV] = useState({ method: '', cashAccount: '', vendor: '', summary: '', amount: '', evidence: false, memo: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => { setV((x) => ({ ...x, [k]: e.target.value })); setErr(null); };
  async function save() {
    if (busy) return;
    if (!clean(v.amount)) { setErr('금액을 넣어 주세요.'); return; }
    setBusy(true);
    try {
      const r = await api.saveCashBook({
        afterId: Number(after.cash_book_id), payDate: after.pay_date, corpId: after.corp_id, method: v.method, cashAccount: v.cashAccount,
        vendor: v.vendor, summary: v.summary, amount: clean(v.amount), evidence: v.evidence ? true : null, memo: v.memo,
      });
      act.inserted(r.id);
    } catch (e) { setErr(errMsg(e)); setBusy(false); }
  }
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); void save(); }
    if (e.key === 'Escape') act.insert(null);
  };
  const cell = 'field h-7 w-full px-1.5 py-0 text-sm';
  const td = 'border-y-2 border-brand-500 bg-brand-50 px-0.5 py-1';
  return (
    <tr onKeyDown={onKey}>
      <td className={`${td} text-center`}>
        <button type="button" className="text-xs text-ink-soft hover:text-red-600" title="닫기 (Esc)" onClick={() => act.insert(null)}>✕</button>
      </td>
      <td className={`${td} px-2 text-xs text-brand-700`}>{after.corp_name} · {after.pay_date.slice(5)}</td>
      <td className={td}><input autoFocus className={cell} list="qe-methods" placeholder="구분" value={v.method} onChange={set('method')} /></td>
      <td className={td}><input className={cell} list="qe-accounts" placeholder="시재계정" value={v.cashAccount} onChange={set('cashAccount')} /></td>
      <td className={td}><input className={cell} list="qe-vendors" placeholder="업체(기사)" value={v.vendor} onChange={set('vendor')} /></td>
      <td className={td}><input className={`${cell} min-w-[24rem]`} placeholder="적요" value={v.summary} onChange={set('summary')} /></td>
      <td className={td}>
        <input className={`${cell} w-24 text-right`} inputMode="numeric" placeholder="금액" value={v.amount}
               onChange={(e) => { const d = e.target.value.replace(/[^\d-]/g, ''); setV((x) => ({ ...x, amount: d && d !== '-' ? Number(d).toLocaleString() : d })); setErr(null); }} />
      </td>
      <td className={`${td} text-center`}>
        <input type="checkbox" className="h-4 w-4 align-middle" checked={v.evidence} title="매입(증빙) 있음"
               onChange={(e) => setV((x) => ({ ...x, evidence: e.target.checked }))} />
      </td>
      <td className={td}><input className={cell} placeholder="memo" value={v.memo} onChange={set('memo')} /></td>
      <td className={td} colSpan={2}>
        <div className="flex items-center gap-1.5">
          <button type="button" className="btn-primary h-7 whitespace-nowrap px-2 py-0 text-xs" disabled={busy} onClick={() => void save()}>
            {busy ? '저장 중…' : '저장 (Enter)'}
          </button>
          {err && <span className="max-w-[12rem] truncate text-xs text-red-600" title={err}>{err}</span>}
        </div>
      </td>
    </tr>
  );
}

const DayRows = memo(function DayRows({ date, rows, sum, onEdit, inline, onPatched, canWrite, sel, act, insertAt, focus }: {
  date: string; rows: CashBookRow[]; sum: number; onEdit: (r: CashBookRow) => void;
  inline: boolean; onPatched: (r: CashBookRow) => void; canWrite: boolean;
  /** 이 날에서 고른 줄 번호(쉼표) — 문자열이라 이 날 선택이 안 바뀌면 다시 안 그린다. */
  sel: string; act: RowActions;
  /** 이 날에서 바로 입력 줄을 열어 둔 줄 번호('' = 없음). */
  insertAt: string;
  /** 링크로 온 줄 번호(이 날에 있을 때만 · '' = 없음) — 굵은 테두리. */
  focus: string;
}) {
  // 🔴 cash_book_id 는 bigint 라 서버가 **문자열**로 준다(타입 선언은 number) — 여기서 Number 로 바꾸면 has() 가 늘 false 가 되어
  //    네모·파란 줄이 안 보였다(09-30 #38 후속, issues 127 과 같은 부류). 문자열로 맞춰 비교한다.
  const picked = useMemo(() => new Set(sel ? sel.split(',') : []), [sel]);
  const grip = (r: CashBookRow) => (
    <td className="select-none whitespace-nowrap px-1 text-center" title="눌러 고르기 · 끌면 여러 줄 · Shift·Ctrl 로 더하기"
        onMouseDown={(e) => act.down(r.cash_book_id, e, true)} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      <span className={`inline-block h-3.5 w-3.5 cursor-pointer rounded-sm border align-middle ${picked.has(String(r.cash_book_id)) ? 'border-sky-600 bg-sky-600' : 'border-ink-faint/60 bg-surface'}`} />
      {canWrite && (
        <span className="invisible ml-0.5 inline-flex align-middle group-hover:visible" onMouseDown={(e) => e.stopPropagation()}>
          <span draggable className="cursor-grab rounded px-0.5 text-xs text-ink-soft hover:bg-canvas active:cursor-grabbing"
                title="잡고 끌어 원하는 자리에 놓기 — 고른 줄이면 고른 줄 전부가 같이 옮겨집니다(같은 법인 줄 사이로)"
                onDragStart={(e) => { e.stopPropagation(); act.moveStart(r, e); }} onDragEnd={() => act.moveEnd()}>⠿</span>
          <button type="button" className="rounded px-0.5 text-xs font-bold text-brand-600 hover:bg-brand-50" title="이 줄 아래에 줄 넣기"
                  onClick={(e) => { e.stopPropagation(); act.insert(r); }}>＋</button>
          <button type="button" className="rounded px-0.5 text-[10px] text-ink-soft hover:bg-canvas" title="한 칸 위로"
                  onClick={(e) => { e.stopPropagation(); act.shift(r, 'up'); }}>▲</button>
          <button type="button" className="rounded px-0.5 text-[10px] text-ink-soft hover:bg-canvas" title="한 칸 아래로"
                  onClick={(e) => { e.stopPropagation(); act.shift(r, 'down'); }}>▼</button>
        </span>
      )}
    </td>
  );
  const copyTd = (label: string, value: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!act.dragged()) act.copyCell(label, value, e.currentTarget as HTMLElement);
  };
  // 날짜 머리줄은 갈색(#38 3번) — 파랑은 「공제 대상」, 아이보리는 「공제 완료」 표시라 겹치지 않는 색으로.
  return (
    <>
      <tr className="border-b-2 border-amber-800/30">
        <td className="bg-amber-100/70 px-2 py-1.5 text-base font-bold text-amber-900" colSpan={6}>{dayLabel(date)}</td>
        <td className="bg-amber-100/70 px-2 text-right text-base font-bold tabular-nums text-amber-900">{money(sum)}</td>
        <td className="bg-amber-100/70" colSpan={4} />
      </tr>
      {inline && rows.map((r) => (<Fragment key={r.cash_book_id}>
        <tr key={r.cash_book_id} data-cash-id={String(r.cash_book_id)} onMouseEnter={() => act.enter(r.cash_book_id)}
            onDragOver={(e) => act.moveOver(r, e)} onDragLeave={(e) => act.moveLeave(e)} onDrop={(e) => act.moveDrop(r, e)}
            className={`group border-b ${r.deduct ? 'text-blue-700' : ''} ${picked.has(String(r.cash_book_id)) ? 'bg-sky-100' : r.deduct_done ? 'bg-amber-50' : ''}`}>
          {grip(r)}
          <td className="px-2 py-0.5">
            <button type="button" className="text-left hover:underline" title="날짜·법인·공제는 창에서 고칩니다" onClick={() => onEdit(r)}>{r.corp_name}</button>
          </td>
          <td className={`px-0.5 ${autoMethod(r.method)}`}><EditCell row={r} field="method" value={r.method ?? ''} onPatched={onPatched} width="w-44" list="qe-methods" /></td>
          <td className="px-0.5"><EditCell row={r} field="cashAccount" value={r.cash_account ?? ''} onPatched={onPatched} width="w-36" list="qe-accounts" /></td>
          <td className="px-0.5"><EditCell row={r} field="vendor" value={r.vendor ?? ''} onPatched={onPatched} width="w-40" list="qe-vendors" /></td>
          <td className="px-0.5"><EditCell row={r} field="summary" value={r.summary ?? ''} onPatched={onPatched} width="w-[34rem]" /></td>
          <td className="px-0.5">
            <EditCell row={r} field="amount" value={String(Math.round(Number(r.amount)))} onPatched={onPatched} width="w-24" numeric
                      disabledReason={r.deduct ? '공제 대상 줄의 금액은 줄을 눌러 창에서 고칩니다(공제액·분납이 같이 바뀝니다)' : undefined} />
          </td>
          <td className="px-0.5 text-center"><EvidenceCheck row={r} onPatched={onPatched} canWrite={canWrite} /></td>
          <td className="px-0.5"><EditCell row={r} field="memo" value={r.memo ?? ''} onPatched={onPatched} width="w-40" /></td>
          <td className="px-2 text-right text-xs">{r.deduct ? money(r.deduct_amount) : ''}</td>
          <td className="px-2">
            <button type="button" className="btn-ghost py-0 text-xs" onClick={() => onEdit(r)}>창</button>
          </td>
        </tr>
        {insertAt === String(r.cash_book_id) && <InsertRow after={r} act={act} />}</Fragment>))}
      {!inline && rows.map((r) => (<Fragment key={r.cash_book_id}>
        <tr key={r.cash_book_id} data-cash-id={String(r.cash_book_id)}
            onDoubleClick={() => { if (!act.dragged()) { window.getSelection()?.removeAllRanges(); onEdit(r); } }}
            onMouseDown={(e) => act.down(r.cash_book_id, e, false)} onMouseEnter={() => act.enter(r.cash_book_id)}
            onDragOver={(e) => act.moveOver(r, e)} onDragLeave={(e) => act.moveLeave(e)} onDrop={(e) => act.moveDrop(r, e)}
            title={`마지막 ${r.updated_by ? r.updated_by.split('@')[0] : '입력'} · ${r.updated_at} — 두 번 누르면 고치기·수정 이력`}
            className={`group cursor-pointer border-b ${r.deduct ? 'text-blue-700' : ''} ${picked.has(String(r.cash_book_id)) ? 'bg-sky-100' : `hover:bg-brand-50/40 ${r.deduct_done ? 'bg-amber-50' : ''}`} ${focus === String(r.cash_book_id) ? 'outline outline-2 -outline-offset-2 outline-amber-500' : ''}`}>
          {grip(r)}
          <td className="px-2 py-1">{r.corp_name}</td>
          <td className={`max-w-[11rem] truncate px-2 ${autoMethod(r.method)}`} title={r.method ?? ''}>{dash(r.method)}</td>
          <td className="max-w-[9rem] truncate px-2" title={r.cash_account ?? ''}>{dash(r.cash_account)}</td>
          <td className="max-w-[11rem] truncate px-2" title={r.vendor ?? ''}>{dash(r.vendor)}</td>
          <td className="cursor-copy px-2 hover:bg-emerald-50" onClick={copyTd('적요', r.summary ?? '')}>
            <div className="flex items-center gap-1">
              <span className="max-w-[44rem] truncate" title={`${r.summary ?? ''}\n— 누르고 Ctrl+C 로 적요만 복사 · 두 번 누르면 창`}>{dash(r.summary)}</span>
              {r.rule_id && <span className="shrink-0 rounded bg-canvas px-1 text-[10px] text-ink-faint" title="매월 반복 출금에서 만든 줄">반복</span>}
              {r.source === 'sheet' && <span className="shrink-0 rounded bg-canvas px-1 text-[10px] text-ink-faint" title="2026-09 출납일보 시트에서 옮겨 온 줄">시트</span>}
              {r.condolence
                ? <CondolenceBadge row={r} onPatched={onPatched} canWrite={canWrite} />
                : r.repair_auto
                ? <span className="shrink-0 rounded bg-violet-100 px-1 text-[10px] font-bold text-violet-800" title="DYOPS 직영수리비 기안 지급일에서 자동으로 들어간 줄 — 지급일이 바뀌면 따라 옮겨집니다">수리비 자동</span>
                : r.repair_linked
                ? <span className="shrink-0 rounded bg-violet-50 px-1 text-[10px] text-violet-700" title={`먼저 손으로 넣은 줄이라 새로 만들지 않고 DYOPS 직영수리비 기안 ${r.repair_linked} 에 연결했습니다`}>수리비 연결</span>
                : hasOpsRef(r.source, r.summary) && <OpsCashBadge cashBookId={r.cash_book_id} source={r.source} />}
            </div>
          </td>
          <td className="cursor-copy px-2 text-right font-medium hover:bg-emerald-50" title="누르고 Ctrl+C 로 금액만 복사 · 두 번 누르면 창"
              onClick={copyTd('금액', String(Math.round(Number(r.amount))))}>{money(r.amount)}</td>
          <td className="px-2 text-center"><EvidenceCheck row={r} onPatched={onPatched} canWrite={canWrite} /></td>
          <td className="max-w-[12rem] cursor-copy truncate px-2 text-xs text-ink-soft hover:bg-emerald-50" title={`${r.memo ?? ''}\n— 누르고 Ctrl+C 로 memo 만 복사 · 두 번 누르면 창`}
              onClick={copyTd('메모', r.memo ?? '')}>{dash(r.memo)}</td>
          <td className="px-2 text-right">
            {r.deduct ? money(r.deduct_amount) : ''}
            {r.deduct_plan && <span className="ml-1 text-[10px]">분납 {r.deduct_plan.length}회</span>}
            {r.deducted && <span className="ml-1 text-[10px] text-emerald-700" title={`재무추가에 넣음: ${r.deducted}`}>재무추가 NO {r.deducted_ids}</span>}
          </td>
          <td className="px-2" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
            {/* #459 후속 — 「미공제 B/ 계정」 창에서 적은 사유는 여기서 보인다(적었는데 볼 곳이 없었다 · 10-30 정여진). */}
            {r.b_skip_reason && !r.deducted && (
              <span className="block max-w-[12rem] truncate text-[11px] text-ink-faint"
                    title={`미공제 사유: ${r.b_skip_reason}
${r.b_skip_by ?? ''} · ${r.b_skip_at ?? ''}
— 「미공제 B/ 계정」 창의 「사유 적은 줄 보기」에서 고치거나 지웁니다`}>
                미공제 사유: {r.b_skip_reason}
              </span>)}
            {r.deduct && (r.deduct_done
              ? <span className="text-xs text-emerald-700" title={r.deducted ? `재무추가에 넣음: ${r.deducted}` : '재무추가에 따로 넣은 줄'}>완료</span>
              : <span className="text-xs text-amber-700" title="「재무추가 공제·대조」에서 넣으면 완료로 바뀝니다">대기</span>)}
          </td>
        </tr>
        {insertAt === String(r.cash_book_id) && <InsertRow after={r} act={act} />}</Fragment>))}
    </>
  );
});

// ─── 입력 폼(출납일보·반복 규칙 공용 칸) ─────────────────────────────────────
interface FormState {
  corpId: number; method: string; cashAccount: string; vendor: string; summary: string; amount: string;
  evidence: '' | 'O' | 'X'; memo: string; deduct: boolean; vatSeparate: boolean; deductAmount: string;
  plan: { month: string; amount: string }[]; deductDone: boolean;
  carAllocId: number | null; allocLabel: string | null; deductAccount: string;
  /** 공제 정산적용월(#57) — null = 자동(지급일 기준, 서버도 같은 식) · '' = 정산월 없음 · YYYY-MM.
   *  monAuto 인 동안은 지급일을 따라 자동값으로 채운다(고르면 멈춘다). */
  deductMon: string | null; monAuto: boolean;
}
const blank = (corpId: number): FormState => ({
  corpId, method: '', cashAccount: '', vendor: '', summary: '', amount: '', evidence: '', memo: '',
  deduct: false, vatSeparate: false, deductAmount: '', plan: [], deductDone: false,
  carAllocId: null, allocLabel: null, deductAccount: '', deductMon: null, monAuto: true,
});
const toInput = (f: FormState): CashBookInput => ({
  corpId: f.corpId, method: f.method, cashAccount: f.cashAccount, vendor: f.vendor, summary: f.summary, amount: clean(f.amount),
  evidence: f.evidence === '' ? null : f.evidence === 'O', memo: f.memo,
  deduct: f.deduct, vatSeparate: f.vatSeparate, deductAmount: f.deduct && f.deductAmount ? clean(f.deductAmount) : null,
  deductPlan: f.deduct && f.plan.length ? f.plan.map((p) => ({ month: p.month, amount: clean(p.amount) })) : null,
  deductDone: f.deductDone,
  carAllocId: f.deduct ? f.carAllocId : null, deductAccount: f.deduct && f.deductAccount ? f.deductAccount : null,
  deductMon: f.deduct && !f.plan.length ? f.deductMon : null,
});

/**
 * 재무추가 계정 — 검색해서 고른다(게시판 #86 · 목록이 길어 드롭다운만으로는 찾기 어렵다).
 * 글자를 넣으면 드롭다운이 맞는 계정만 남기고 아래에 버튼으로도 보인다 · Enter = 첫 번째.
 */
export function DeductAccountPicker({ value, options, onPick }: { value: string; options: string[]; onPick: (v: string) => void }) {
  const [q, setQ] = useState('');
  const all = useMemo(() => [...new Set([...(value ? [value] : []), ...options])], [value, options]);
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hits = words.length ? all.filter((v) => words.every((w) => v.toLowerCase().includes(w))) : all;
  const shown = value && !hits.includes(value) ? [value, ...hits] : hits;
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2"><span className="w-20">계정</span>
        <input className="field w-40" placeholder="계정 검색" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (hits[0]) { onPick(hits[0]); setQ(''); } } }} />
        <select className="field w-56" value={value} onChange={(e) => onPick(e.target.value)}>
          <option value="">{words.length ? `(${hits.length}개 찾음)` : '(고르세요)'}</option>
          {shown.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>
      {words.length > 0 && (
        <div className="flex flex-wrap gap-1 pl-[5.5rem]">
          {hits.length === 0 ? <span className="text-xs text-ink-faint">맞는 계정이 없습니다</span>
            : hits.slice(0, 12).map((v) => (
              <button key={v} type="button" className={`rounded border px-2 py-0.5 text-xs hover:bg-canvas ${v === value ? 'border-brand-500 font-semibold' : 'border-line'}`}
                onClick={() => { onPick(v); setQ(''); }}>{v}</button>
            ))}
          {hits.length > 12 && <span className="text-xs text-ink-faint">외 {hits.length - 12}개 — 더 입력하세요</span>}
        </div>
      )}
    </div>
  );
}

function Fields({ f, setF, corps, withPlan, deductedIds, payDate }: {
  f: FormState; setF: (u: (x: FormState) => FormState) => void; corps: AcctCorp[]; withPlan: boolean;
  /** 재무추가에 넣은 줄이면 그 NO — 공제 상태 표시용. */
  deductedIds?: string | null;
  /** 출납 지급일 — 정산적용월 기본값(6일~5일)의 기준. 반복 규칙 폼엔 없다. */
  payDate?: string;
}) {
  const hints = useHints(f.corpId || null);
  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((x) => ({ ...x, [k]: k === 'corpId' ? Number(e.target.value) : e.target.value }));
  const autoDeduct = f.vatSeparate ? supply(f.amount) : clean(f.amount);
  // #186 「3개월 분할 공제」 — 공제액을 N개월로 똑같이 나눠 분납 칸을 채운다(원 단위 · 나머지는 마지막 달).
  const [splitN, setSplitN] = useState(3);
  // 정산적용월(#57): 13칸 = 정산월 없음 + 기본 정산월 앞 6·뒤 5개월. 마감된 달은 고르면 이유를 띄우고 되돌린다.
  const autoMon = payDate ? autoDeductMon(payDate, hints.finishedMons) : '';
  // 자동인 동안은 지급일·마감 목록을 따라간다. 재무추가에 이미 넣은 줄은 저장된 값(null = 자동)을 그대로 둔다 —
  // 채워 넣으면 「정산적용월이 바뀌었다」로 읽혀 넣은 줄을 되돌리고 다시 넣는다.
  useEffect(() => {
    if (withPlan && f.monAuto && !deductedIds && autoMon && f.deductMon !== autoMon) setF((x) => ({ ...x, deductMon: autoMon }));
  }, [withPlan, f.monAuto, deductedIds, autoMon, f.deductMon, setF]);
  const monValue = f.deductMon ?? autoMon;
  const monOptions = useMemo(() => {
    if (!payDate) return [];
    const base = defaultDeductMon(payDate);
    const list = Array.from({ length: 12 }, (_, i) => addMonths(base, i - 6));
    if (monValue && !list.includes(monValue)) list.push(monValue);
    return list.sort();
  }, [payDate, monValue]);
  function pickMon(v: string) {
    if (v !== '' && hints.finishedMons.includes(v)) {
      window.alert(`${monLabel(v)}은 기사 정산마감이 끝난 달이라 정산적용월로 고를 수 없습니다. 다른 달을 골라 주세요.`);
      return;
    }
    setF((x) => ({ ...x, deductMon: v, monAuto: false }));
  }
  return (
    <>
      <fieldset className="grid grid-cols-2 gap-3 text-sm">
        <label className="space-y-1">법인
          <select className="field w-full" value={f.corpId} onChange={set('corpId')}>
            {corps.map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name} · {c.name}</option>)}
          </select>
        </label>
        <label className="space-y-1">구분(인출계좌/더존계정)
          <input className="field w-full" list="cb-methods" value={f.method} onChange={set('method')} placeholder="미지급금▶5일" />
        </label>
        <label className="space-y-1">시재계정(유동성)
          <input className="field w-full" list="cb-accounts" value={f.cashAccount} onChange={set('cashAccount')} />
        </label>
        <label className="space-y-1">업체(기사)
          <input className="field w-full" list="cb-vendors" value={f.vendor} onChange={set('vendor')} />
        </label>
        <label className="col-span-2 space-y-1">적요<input className="field w-full" value={f.summary} onChange={set('summary')} /></label>
        <label className="space-y-1">금액
          <MoneyInput className="w-full" value={f.amount} onChange={(a) => setF((x) => ({ ...x, amount: a }))} />
        </label>
        <label className="space-y-1">매입(증빙)
          <select className="field w-full" value={f.evidence} onChange={set('evidence')}>
            <option value="">해당 없음</option><option value="O">O (증빙 있음)</option><option value="X">X (증빙 없음)</option>
          </select>
        </label>
        <label className="col-span-2 space-y-1">memo(받는 계좌 등)<input className="field w-full" value={f.memo} onChange={set('memo')} /></label>
      </fieldset>
      <datalist id="cb-methods">{hints.methods.map((v) => <option key={v} value={v} />)}</datalist>
      <datalist id="cb-accounts">{hints.cashAccounts.map((v) => <option key={v} value={v} />)}</datalist>
      <datalist id="cb-vendors">{hints.vendors.map((v) => <option key={v} value={v} />)}</datalist>

      <div className="space-y-2 rounded-lg border p-3 text-sm">
        <div className="grid grid-cols-2 items-center gap-3">
          <label className="flex items-center gap-2 font-medium text-blue-700">
            <input type="checkbox" checked={f.deduct} onChange={(e) => setF((x) => ({ ...x, deduct: e.target.checked }))} />
            버스인 공제 대상(기사에게서 공제)
          </label>
          {withPlan && f.deduct && payDate && (
            <label className="flex items-center gap-2">
              <span className="shrink-0">정산적용월</span>
              {f.plan.length > 0
                ? <span className="text-ink-soft">분납 정산월대로</span>
                : (
                  <select className="field w-full" value={monValue} onChange={(e) => pickMon(e.target.value)}
                          title="재무추가에 들어갈 정산월 — 지급일 6일~다음 달 5일이 그 달로 먼저 잡힙니다">
                    <option value="">정산월 없음</option>
                    {monOptions.map((m) => (
                      <option key={m} value={m}>{monLabel(m)}{hints.finishedMons.includes(m) ? ' (마감)' : ''}</option>
                    ))}
                  </select>
                )}
            </label>
          )}
        </div>
        {f.deduct && (
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={f.vatSeparate} onChange={(e) => setF((x) => ({ ...x, vatSeparate: e.target.checked }))} />
              부가세 별도(공급가로 공제)
            </label>
            <label className="space-y-1">공제액
              <input className="field w-full text-right" value={f.deductAmount} placeholder={autoDeduct ? `자동 ${Number(autoDeduct).toLocaleString()}` : '자동'}
                     onChange={set('deductAmount')} />
            </label>
            {withPlan && (
              <div className="col-span-2 space-y-1">
                <div className="flex items-center gap-2">
                  <span>분납(전액 지급 후 버스인엔 정산월별로 나눠 공제)</span>
                  <button type="button" className="btn-ghost py-0.5 text-xs"
                          onClick={() => setF((x) => ({ ...x, plan: [...x.plan, { month: x.plan.length ? nextMonth(x.plan[x.plan.length - 1]!.month) : monthOf(kst()), amount: '' }] }))}>
                    + 달 추가
                  </button>
                  <span className="ml-auto flex items-center gap-1 text-xs">
                    <input type="number" min={2} max={24} className="field w-16 py-0.5 text-right" value={splitN}
                           onChange={(e) => setSplitN(Math.max(2, Math.min(24, Math.trunc(Number(e.target.value)) || 2)))} />
                    개월로
                    <button type="button" className="btn-ghost py-0.5 text-xs"
                            title="공제액을 정산적용월부터 똑같이 나눠 채웁니다(원 단위 · 나머지는 마지막 달)"
                            onClick={() => {
                              const total = Math.round(Number(clean(f.deductAmount) || autoDeduct || 0));
                              if (!(total > 0)) return;
                              const base = Math.floor(total / splitN);
                              let m = monValue || monthOf(kst());
                              const plan: { month: string; amount: string }[] = [];
                              for (let i = 0; i < splitN; i++) {
                                plan.push({ month: m, amount: String(i === splitN - 1 ? total - base * (splitN - 1) : base) });
                                m = nextMonth(m);
                              }
                              setF((x) => ({ ...x, plan }));
                            }}>
                      나누기
                    </button>
                  </span>
                </div>
                {f.plan.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="month" className="field w-40" value={p.month}
                           onChange={(e) => setF((x) => ({ ...x, plan: x.plan.map((q, j) => (j === i ? { ...q, month: e.target.value } : q)) }))} />
                    <MoneyInput className="w-40" value={p.amount} placeholder="금액"
                           onChange={(a) => setF((x) => ({ ...x, plan: x.plan.map((q, j) => (j === i ? { ...q, amount: a } : q)) }))} />
                    <button type="button" className="btn-ghost py-0.5 text-xs" onClick={() => setF((x) => ({ ...x, plan: x.plan.filter((_, j) => j !== i) }))}>빼기</button>
                  </div>
                ))}
                {f.plan.length > 0 && (
                  <div className="text-xs text-ink-soft">
                    분납 합계 {f.plan.reduce((s, p) => s + (Number(clean(p.amount)) || 0), 0).toLocaleString()} ·
                    공제액 {Number(clean(f.deductAmount) || autoDeduct || 0).toLocaleString()} — 같아야 저장됩니다.
                  </div>
                )}
              </div>
            )}
            {withPlan && (
              <div className="col-span-2 space-y-2 rounded bg-canvas p-2">
                <div className="text-xs text-ink-soft">재무추가에 바로 넣으려면 — 누구에게서(편성)·어느 계정으로. 분납이 없으면 위 정산적용월에 공제합니다.</div>
                <AllocPicker value={f.carAllocId} label={f.allocLabel} onPick={(id) => setF((x) => ({ ...x, carAllocId: id, allocLabel: null }))} />
                <DeductAccountPicker value={f.deductAccount} options={hints.deductAccounts}
                  onPick={(v) => setF((x) => ({ ...x, deductAccount: v }))} />
              </div>
            )}
            {/* 공제 완료는 손으로 체크하지 않는다(09-29 way) — 저장할 때 재무추가에 넣으면서 완료로 바꾼다. 여기선 상태만 보인다. */}
            {withPlan && (
              <p className="text-sm">
                {deductedIds ? <span className="text-emerald-700">공제 완료 — 재무추가 NO {deductedIds}</span>
                  : f.deductDone ? <span className="text-emerald-700">공제 완료(재무추가에 따로 넣은 줄)</span>
                  : <span className="text-amber-700">공제 대기 — 편성·계정을 넣고 저장하면 재무추가에 바로 들어갑니다.</span>}
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}

/** 이력 칸 이름 — 서버 트리거의 추적 칸과 같은 목록(`_cashbook.sql cash_book_log_trg`). */
const LOG_LABEL: Record<string, string> = {
  corp_id: '법인', pay_date: '일자', method: '구분', cash_account: '시재계정', vendor: '업체(기사)', summary: '적요', amount: '금액',
  evidence: '증빙', memo: 'memo', deduct: '공제 대상', vat_separate: '부가세 별도', deduct_amount: '공제액', deduct_plan: '분납',
  deduct_done: '공제 완료', car_alloc_id: '공제 편성', deduct_account: '재무추가 계정', deduct_mon: '정산적용월',
};
const MONEY_COLS = new Set(['amount', 'deduct_amount']);
function logValue(col: string, v: unknown, corps: AcctCorp[]): string {
  if (v === null || v === undefined || v === '') return col === 'deduct_mon' && v === '' ? '정산월 없음' : '(빈칸)';
  if (col === 'evidence') return v ? 'O' : 'X';
  if (typeof v === 'boolean') return v ? '예' : '아니오';
  if (MONEY_COLS.has(col)) return `${Math.round(Number(v)).toLocaleString()}원`;
  if (col === 'corp_id') return corps.find((c) => c.corp_id === Number(v))?.short_name ?? String(v);
  if (col === 'deduct_plan' && Array.isArray(v)) return v.map((p: { month?: string; amount?: string }) => `${p.month} ${Number(p.amount).toLocaleString()}`).join(', ') || '(없음)';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

/**
 * 수정 이력(#38) — 누가·언제·무엇을(전 → 후). 창을 열 때 한 번 읽는다. 이력 표(09-29) 이전의 고침은 기록이 없다.
 */
function CashBookHistory({ id, corps }: { id: number; corps: AcctCorp[] }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.listCashBookLog>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.listCashBookLog({ id }).then((r) => { if (!cancelled) setData(r); }).catch((e) => { if (!cancelled) setError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [id]);
  const edits = data?.logs.filter((l) => l.action !== 'insert') ?? [];
  const last = edits[0];
  const actLabel = { insert: '입력', update: '고침', delete: '지움', restore: '되살림' } as const;
  return (
    <div className="rounded-lg border p-3 text-sm">
      <button type="button" className="flex w-full items-center gap-2 text-left" onClick={() => setOpen((x) => !x)}>
        <span className="font-medium">수정 이력</span>
        <span className="text-xs text-ink-soft">
          {error ? '못 읽었습니다' : !data ? '읽는 중…'
            : last ? `마지막 ${last.changed_by ?? '?'} · ${last.changed_at} · 모두 ${edits.length}번 고침`
            : '고친 기록 없음'}
        </span>
        <span className="ml-auto text-xs text-ink-faint">{open ? '접기 ▲' : '펼치기 ▼'}</span>
      </button>
      {open && data && (
        <ul className="mt-2 max-h-60 space-y-1.5 overflow-y-auto text-xs">
          {data.logs.map((l) => (
            <li key={l.log_id} className="border-t pt-1.5">
              <span className="font-medium">{l.changed_by ?? '?'}</span>
              <span className="ml-1 text-ink-faint">{l.changed_at}</span>
              <span className={`ml-1 ${l.action === 'delete' ? 'text-red-600' : 'text-ink-soft'}`}>{actLabel[l.action]}</span>
              {l.changes && Object.entries(l.changes).map(([col, [a, b]]) => (
                <div key={col} className="pl-3 text-ink-soft">
                  {LOG_LABEL[col] ?? col}: <span className="line-through">{logValue(col, a, corps)}</span> → <b className="text-ink">{logValue(col, b, corps)}</b>
                </div>
              ))}
            </li>
          ))}
          {data.first && !data.logs.some((l) => l.action === 'insert') && (
            <li className="border-t pt-1.5 text-ink-soft">
              <span className="font-medium">{data.first.created_by ?? '?'}</span>
              <span className="ml-1 text-ink-faint">{data.first.created_at}</span>
              <span className="ml-1">{data.first.source === 'sheet' ? '시트에서 옮겨 옴' : '입력'}</span>
              <div className="pl-3 text-ink-faint">이력 기록(2026-09-29) 이전의 고침은 남아 있지 않습니다.</div>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function CashBookForm({ corps, row, defaultCorp, defaultDate, afterId, onClose, onSaved }: {
  corps: AcctCorp[]; row: CashBookRow | null; defaultCorp: number; defaultDate: string; onClose: () => void; onSaved: () => void;
  /** 「아래에 줄 넣기」(#38) — 이 줄 바로 아래에 넣는다. 이어서 입력하면 방금 넣은 줄 아래로 이어 붙는다. */
  afterId?: number;
}) {
  const [payDate, setPayDate] = useState(row?.pay_date ?? defaultDate);
  const [f, setF] = useState<FormState>(row ? {
    corpId: row.corp_id, method: row.method ?? '', cashAccount: row.cash_account ?? '', vendor: row.vendor ?? '', summary: row.summary ?? '',
    amount: Number(row.amount).toLocaleString(), evidence: row.evidence === null ? '' : row.evidence ? 'O' : 'X', memo: row.memo ?? '',
    deduct: row.deduct, vatSeparate: row.vat_separate, deductAmount: row.deduct_amount ? Number(row.deduct_amount).toLocaleString() : '',
    plan: (row.deduct_plan ?? []).map((p) => ({ month: p.month, amount: Number(p.amount).toLocaleString() })), deductDone: row.deduct_done,
    carAllocId: row.car_alloc_id, allocLabel: row.alloc_label, deductAccount: row.deduct_account ?? '',
    deductMon: row.deduct_mon, monAuto: row.deduct_mon === null,
  } : blank(defaultCorp));
  const [undoArm, setUndoArm] = useState(false);
  const [after, setAfter] = useState(afterId);
  async function undo() {
    if (!row) return;
    if (!undoArm) { setUndoArm(true); return; }
    setUndoArm(false); setBusy(true); setError(null);
    try { const r = await api.undoCashBookDeduct({ id: Number(row.cash_book_id) }); setNotice(`재무추가 공제 ${r.removed}줄을 지웠습니다.`); onSaved(); setF((x) => ({ ...x, deductDone: false })); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);
  const [confirmDel, setConfirmDel] = useState(false);

  async function addManual() {
    if (!row) return;
    setBusy(true); setError(null);
    try { await api.commitCashBookDeduct({ ids: [Number(row.cash_book_id)], clearManual: true }); onSaved(); onClose(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }

  async function save(keepOpen: boolean) {
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.saveCashBook({ id: row?.cash_book_id, afterId: row ? undefined : after, payDate, ...toInput(f) });
      if (!row && after) setAfter(r.id);
      onSaved();
      // 저장하면 재무추가에 바로 들어간다(09-29). 못 들어가면 창을 닫지 않고 이유를 보인다 — 대조 창에서 다시 볼 수 있다.
      const pend = r.deduct?.state === 'pending' ? `저장했습니다. 재무추가는 대기 — ${r.deduct.reason ?? ''}` : null;
      if (pend && !keepOpen) { setNotice(pend); setF((x) => ({ ...x, deductDone: false })); return; }
      if (keepOpen) {
        // 같은 날·같은 법인·같은 구분으로 이어서 키인(시트에서 한 날짜 아래 여러 줄 쓰던 흐름).
        setF((x) => ({ ...blank(x.corpId), method: x.method, cashAccount: x.cashAccount }));
        setNotice(pend ?? (r.deduct?.state === 'done' ? '저장하고 재무추가에 넣었습니다. 이어서 입력하세요.' : '저장했습니다. 이어서 입력하세요.'));
      } else onClose();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }

  async function remove() {
    if (!row) return;
    setBusy(true); setError(null);
    try {
      await api.deleteCashBook({ id: row.cash_book_id });
      onSaved(); onClose();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <div className="card max-h-[90vh] space-y-4 overflow-y-auto p-5">
      <div className="flex items-center gap-3">
        <h2 className="font-semibold">{row ? '출납 고치기' : afterId ? '출납 입력 — 고른 줄 아래에' : '출납 입력'}</h2>
        <input type="date" className="field w-40" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
        {row?.rule_id && <span className="text-xs text-ink-faint">반복 출금 {row.rule_month} 에서 만든 줄</span>}
      </div>
      {row?.deducted && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          재무추가에 넣은 줄입니다({row.deducted}) — 금액·공제·편성·계정은 되돌린 뒤에 고칩니다.
          <button className={`btn-ghost ml-auto text-xs ${undoArm ? 'text-red-600' : ''}`} disabled={busy} onClick={() => void undo()} onBlur={() => setUndoArm(false)}>
            {undoArm ? '재무추가 줄을 지웁니다 — 한 번 더' : '공제 되돌리기'}
          </button>
        </div>
      )}
      {/* 손 「공제 완료」 체크가 있던 때 웹에서 체크만 한 줄(게시판 #48) — 표시를 풀고 재무추가에 넣는다. 시트에서 온 줄은 버스인에서 공제됐을 수 있어 안 보인다. */}
      {row && row.deduct && row.deduct_done && !row.deducted && row.source === 'web' && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          공제 완료로 표시됐지만 재무추가에는 아직 없는 줄입니다.
          <button className="btn-primary ml-auto text-xs" disabled={busy} onClick={() => void addManual()}>재무추가에 넣기</button>
        </div>
      )}
      <Fields f={f} setF={setF} corps={corps} withPlan deductedIds={row?.deducted_ids} payDate={payDate} />
      {row && <CashBookHistory id={row.cash_book_id} corps={corps} />}
      {error && <ErrorBox message={error} />}
      <div className="flex flex-wrap gap-2">
        <button className="btn-primary" onClick={() => save(false)} disabled={busy}>{busy ? '저장 중…' : '저장'}</button>
        {!row && <button className="btn" onClick={() => save(true)} disabled={busy}>저장 후 계속 입력</button>}
        <button className="btn-ghost" onClick={onClose}>닫기</button>
        {row && (confirmDel ? (
          <span className="ml-auto flex gap-1">
            <button className="btn-ghost text-red-600" onClick={remove} disabled={busy}>정말 지우기</button>
            <button className="btn-ghost" onClick={() => setConfirmDel(false)}>아니오</button>
          </span>
        ) : <button className="btn-ghost ml-auto text-red-600" onClick={() => setConfirmDel(true)}>지우기</button>)}
      </div>
    </div>
  );
}

/**
 * 출납일보 대량 등록 양식(#38 1번) — 시트와 같은 칸 순서라 `parseCashPaste` 가 머리줄 「일자 (요일)」 로 그대로 읽는다.
 * 소계 칸은 시트 모양을 맞추려고 둔 빈칸(읽지 않는다). 일자는 줄마다 적어도 되고 첫 줄에만 적어도 된다.
 */
async function downloadCashTemplate(date: string) {
  const XLSX = await import('xlsx');
  const aoa = [
    ['※ 예시 줄은 지우고 적어 주세요. 법인은 올리는 창에서 고릅니다(한 파일 = 한 법인). 금액 칸은 숫자만(콤마 가능), 증빙 = O / X / 빈칸.'],
    ['일자 (요일)', '구분(인출계좌/계정)', '시재계정', '업체(기사)', '적요', '금액', '소계', '매입(증빙)여부', 'memo'],
    [date, '미지급금▶5일', '보통예금', '○○정비', '차량 수리비', '550,000', '', 'O', '국민 123-456'],
    [date, '현금', '현금', '○○마트', '사무용품', '32,000', '', 'X', ''],
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [12, 18, 10, 14, 24, 12, 8, 12, 18].map((wch) => ({ wch }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '출납일보');
  XLSX.writeFile(wb, '출납일보_대량등록_양식.xlsx');
}

/**
 * 붙여넣기 — 시트·엑셀에서 여러 줄을 복사해 한 번에(2026-09-28 way). 칸 해석은 `lib/cashPaste.ts`.
 * 미리보기를 보고 넣는다 · 서버는 전부 아니면 전무. 공제 표시는 넣은 뒤 줄을 눌러 단다.
 */
function PasteForm({ corps, defaultCorp, defaultDate, onClose, onSaved }: {
  corps: AcctCorp[]; defaultCorp: number; defaultDate: string; onClose: () => void; onSaved: () => void;
}) {
  const [corpId, setCorpId] = useState<number | null>(defaultCorp || null);
  const [date, setDate] = useState(defaultDate);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);
  const parsed = useMemo(() => parseCashPaste(text, date), [text, date]);
  const sum = parsed.rows.reduce((s, r) => s + Number(r.amount), 0);

  async function save() {
    if (!corpId) { setError('법인을 고르세요.'); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.pasteCashBook({
        corpId,
        rows: parsed.rows.map((p) => ({
          payDate: p.payDate, method: p.method, cashAccount: p.cashAccount, vendor: p.vendor, summary: p.summary,
          amount: p.amount, evidence: p.evidence, memo: p.memo,
        })),
      });
      onSaved();
      setText('');
      setNotice(`${r.added}줄(${money(r.amount)})을 넣었습니다. 이어서 다른 법인·날짜를 붙여넣을 수 있습니다.`);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }

  /** 양식 파일(#38 1번) → 붙여넣기와 같은 글(탭·줄바꿈)로 바꿔 아래 칸에 넣는다 — 미리보기·저장 경로를 하나로 둔다. */
  async function pickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null); setNotice(null);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]!]!;
      const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: '', raw: true, blankrows: false });
      const cellOf = (v: unknown) => (v instanceof Date
        ? `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`
        : String(v ?? '').replace(/[\t\r\n]+/g, ' ').trim());
      // 안내 줄(※)은 버린다. 머리줄 「일자 (요일)」 은 남겨야 칸 위치를 그걸로 읽는다.
      setText(aoa.map((r) => r.slice(0, 9).map(cellOf)).filter((r) => !r[0]!.startsWith('※')).map((r) => r.join('\t')).join('\n'));
    } catch (err) { setError(`파일을 읽지 못했습니다: ${errMsg(err)}`); } finally { e.target.value = ''; }
  }

  const td = 'px-2 py-1';
  return (
    <div className="card max-h-[90vh] space-y-3 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-semibold">붙여넣기·엑셀 올리기</h2>
        <CorpSelect corps={corps} value={corpId} onChange={setCorpId} all={false} />
        <input type="date" className="field w-40" value={date} onChange={(e) => setDate(e.target.value)}
               title="일자 칸이 비었거나 일자 칸 없이 복사한 줄에 쓰는 날짜" />
        <span className="ml-auto flex gap-2">
          <button type="button" className="btn text-xs" onClick={() => void downloadCashTemplate(date)}>양식 받기</button>
          <label className="btn cursor-pointer text-xs">
            엑셀 파일 올리기
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => void pickFile(e)} />
          </label>
        </span>
      </div>
      <p className="text-xs text-ink-soft">
        출납일보 시트에서 <b>일자부터 memo 까지</b>(또는 구분부터) 줄을 골라 복사한 뒤 아래 칸에 붙여넣으세요.
        날짜만 적힌 줄 아래는 그 날짜로, 날짜가 없으면 위에서 고른 날짜로 들어갑니다.
        많은 줄은 <b>「양식 받기」</b>로 받은 파일에 적어 <b>「엑셀 파일 올리기」</b> — 한 파일 = 위에서 고른 법인 하나입니다.
      </p>
      <textarea className="field h-40 w-full font-mono text-xs" placeholder="여기에 붙여넣기 (Ctrl+V)"
                value={text} onChange={(e) => { setText(e.target.value); setNotice(null); }} />
      {text.trim() !== '' && (
        <div className="space-y-1">
          <p className="text-sm">
            <b>{parsed.rows.length}줄</b> · 합계 <b>{money(String(sum))}</b>
            {parsed.skipped.length > 0 && (
              <span className="ml-2 text-amber-700" title={`붙여넣은 ${parsed.skipped.join(', ')}번째 줄`}>
                금액이 없거나 내용이 빈 {parsed.skipped.length}줄은 뺐습니다
              </span>
            )}
          </p>
          {parsed.rows.length > 0 && (
            <div className="max-h-72 overflow-auto rounded-lg border border-line">
              <table className="w-full whitespace-nowrap text-xs">
                <thead>
                  <tr className="text-left text-ink-soft">
                    {['일자', '구분', '시재계정', '업체(기사)', '적요', '금액', '증빙', 'memo'].map((h) => (
                      <th key={h} className={`sticky top-0 bg-surface ${td} font-medium ${h === '금액' ? 'text-right' : ''}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.map((r) => (
                    <tr key={r.line} className="border-t border-line">
                      <td className={td}>{r.payDate}</td><td className={td}>{dash(r.method)}</td><td className={td}>{dash(r.cashAccount)}</td>
                      <td className={td}>{dash(r.vendor)}</td><td className={`${td} max-w-xs truncate`} title={r.summary}>{dash(r.summary)}</td>
                      <td className={`${td} text-right tabular-nums`}>{money(r.amount)}</td>
                      <td className={td}>{r.evidence === null ? '' : r.evidence ? 'O' : 'X'}</td>
                      <td className={`${td} max-w-[10rem] truncate`} title={r.memo}>{r.memo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {error && <ErrorBox message={error} />}
      <div className="flex gap-2">
        <button className="btn-primary" onClick={() => void save()} disabled={busy || parsed.rows.length === 0 || !corpId}>
          {busy ? '넣는 중…' : `${parsed.rows.length}줄 넣기`}
        </button>
        <button className="btn-ghost" onClick={onClose}>닫기</button>
      </div>
    </div>
  );
}

/**
 * #19 3번 — 「당일 출납일보 공제금액 합계와 버스인 공제금액 합계를 비교해서 오류를 찾아낼 수 있는 기능」 + 재무추가에 바로 넣기.
 * 대조는 스위치와 상관없이 본다. 넣기는 관리자가 스위치를 켠 뒤(버스인웹 전환 뒤)만 — 두 번 눌러야 된다.
 */
/**
 * #460 한 번에 공제하기 — 고른 줄을 한 창에서 편성·계정·정산적용월(·공제액)을 정해 한 번에 재무추가에 넣는다.
 * 편성·계정은 서버가 추천해 채운다(번호판 → 그날 진행 편성 · 같은 시재계정에서 가장 많이 고른 계정) — 사람이 보고 바꾼다.
 * 한 줄이라도 걸리면 아무것도 안 들어간다(서버가 어느 줄인지 알려 준다).
 */
type BulkLine = {
  row: CashBulkDeductRow; allocId: number | null; allocLabel: string | null; allocSug: boolean;
  account: string; mon: string | null; vat: boolean; amt: string; open: boolean;
};
function BulkDeductPanel({ ids, onClose, onDone }: { ids: number[]; onClose: () => void; onDone: (msg: string) => void }) {
  const hints = useHints(null);
  const [lines, setLines] = useState<BulkLine[] | null>(null);
  const [skipped, setSkipped] = useState<{ row: CashBulkDeductRow; why: string }[]>([]);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allAcct, setAllAcct] = useState('');
  useEffect(() => {
    let alive = true;
    api.prepareCashBookBulkDeduct({ ids }).then((r) => {
      if (!alive) return;
      setOpen(r.open);
      const sk: { row: CashBulkDeductRow; why: string }[] = [];
      const ls: BulkLine[] = [];
      for (const row of r.rows) {
        if (row.linked > 0) { sk.push({ row, why: '이미 재무추가에 넣은 줄' }); continue; }
        if (row.deduct && row.deduct_done) { sk.push({ row, why: '공제 완료로 표시된 줄' }); continue; }
        if (!(Number(row.amount) > 0)) { sk.push({ row, why: '0 이하 금액' }); continue; }
        const allocId = row.car_alloc_id ?? row.sug_alloc_id;
        ls.push({
          row, allocId, allocLabel: row.car_alloc_id ? row.alloc_label : row.sug_alloc_label, allocSug: !row.car_alloc_id && !!row.sug_alloc_id,
          account: row.deduct_account ?? row.sug_account ?? '', mon: row.deduct ? row.deduct_mon : null,
          vat: row.deduct ? row.vat_separate : false,
          amt: row.deduct && row.deduct_amount ? Number(row.deduct_amount).toLocaleString() : '', open: !allocId,
        });
      }
      setLines(ls); setSkipped(sk);
    }).catch((e) => { if (alive) setError(errMsg(e)); });
    return () => { alive = false; };
  }, [ids]);
  const upd = (i: number, u: Partial<BulkLine>) => setLines((ls) => ls && ls.map((l, j) => (j === i ? { ...l, ...u } : l)));
  const autoAmt = (l: BulkLine) => (l.vat ? supply(l.row.amount) : clean(l.row.amount));
  const amtOf = (l: BulkLine) => Number(clean(l.amt) || autoAmt(l) || 0);
  const monOf = (l: BulkLine) => l.mon ?? autoDeductMon(l.row.pay_date, hints.finishedMons);
  const missing = (lines ?? []).filter((l) => !l.allocId || !l.account).length;
  const total = (lines ?? []).reduce((s, l) => s + (l.row.plan_n > 0 ? Number(l.row.deduct_amount ?? 0) : amtOf(l)), 0);
  function pickMon(i: number, v: string) {
    if (v !== '' && hints.finishedMons.includes(v)) {
      window.alert(`${monLabel(v)}은 기사 정산마감이 끝난 달이라 정산적용월로 고를 수 없습니다. 다른 달을 골라 주세요.`);
      return;
    }
    upd(i, { mon: v });
  }
  async function commit() {
    if (!lines?.length || missing) return;
    setBusy(true); setError(null);
    try {
      const r = await api.bulkCashBookDeduct({ items: lines.map((l) => ({
        id: Number(l.row.cash_book_id), carAllocId: l.allocId!, deductAccount: l.account,
        deductMon: l.row.plan_n > 0 ? null : monOf(l), vatSeparate: l.vat,
        deductAmount: l.row.plan_n > 0 ? null : (clean(l.amt) || null),
      })) });
      onDone(r.open
        ? `${r.rows}줄을 재무추가에 넣었습니다 — 재무추가 ${r.created}줄 · ${Number(r.amount).toLocaleString()}원`
        : `${r.rows}줄을 공제 대상으로 저장했습니다(바로 공제가 꺼져 있어 재무추가는 대기).`);
    } catch (e) { setError(errMsg(e)); setBusy(false); }
  }
  const td = 'px-2 py-1 align-top';
  return (
    <div className="card max-h-[90vh] space-y-3 overflow-y-auto p-5 text-sm">
      <h2 className="font-bold">한 번에 공제하기 <span className="font-normal text-ink-soft">{lines ? `${lines.length}줄` : ''}</span></h2>
      <p className="text-ink-soft">
        줄마다 누구에게서(편성)·어느 계정으로·몇 월 정산에 공제할지 정하고 아래 버튼을 누르면 한 번에 재무추가에 들어갑니다.
        편성·계정은 적요의 차량번호와 지난 공제를 보고 미리 채웠습니다(노란 「추천」) — 맞는지 보시고 다르면 바꿔 주세요.
      </p>
      {!open && <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-800">바로 공제가 꺼져 있어 지금은 공제 대상으로 저장만 됩니다.</p>}
      {error && <ErrorBox message={error} />}
      {!lines && !error && <p className="text-ink-soft">불러오는 중…</p>}
      {lines && lines.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-canvas px-3 py-2">
            <span>모든 줄 계정</span>
            <select className="field h-8 w-40 py-0" value={allAcct} onChange={(e) => setAllAcct(e.target.value)}>
              <option value="">고르기</option>
              {hints.deductAccounts.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <button className="btn py-0.5" disabled={!allAcct} onClick={() => setLines((ls) => ls && ls.map((l) => ({ ...l, account: allAcct })))}>모두 바꾸기</button>
            <span className="ml-auto tabular-nums">공제 합계 <b>{total.toLocaleString()}</b>원</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-ink-soft">
                <th className="px-2 py-1">일자</th><th className="px-2 py-1">업체(기사) · 적요</th><th className="px-2 py-1 text-right">금액</th>
                <th className="px-2 py-1">공제액</th><th className="px-2 py-1">편성</th><th className="px-2 py-1">계정</th><th className="px-2 py-1">정산적용월</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const base = defaultDeductMon(l.row.pay_date);
                const mv = monOf(l);
                const monOpts = Array.from({ length: 12 }, (_, k) => addMonths(base, k - 6));
                if (mv && !monOpts.includes(mv)) monOpts.push(mv);
                monOpts.sort();
                return (
                  <Fragment key={l.row.cash_book_id}>
                    <tr className={`border-b ${!l.allocId || !l.account ? 'bg-amber-50/60' : ''}`}>
                      <td className={`${td} whitespace-nowrap`}>{l.row.pay_date.slice(5)}<div className="text-xs text-ink-soft">{l.row.corp_name}</div></td>
                      <td className={`${td} max-w-[22rem]`}>
                        <div className="truncate font-medium" title={l.row.vendor ?? ''}>{dash(l.row.vendor)}</div>
                        <div className="truncate text-xs text-ink-soft" title={l.row.summary ?? ''}>{dash(l.row.summary)}</div>
                      </td>
                      <td className={`${td} text-right tabular-nums`}>{money(l.row.amount)}</td>
                      <td className={`${td} whitespace-nowrap`}>
                        {l.row.plan_n > 0 ? <span className="text-xs">분납 {l.row.plan_n}회 그대로</span> : (
                          <>
                            <input className="field h-8 w-28 py-0 text-right" value={l.amt} placeholder={Number(autoAmt(l) || 0).toLocaleString()}
                                   onChange={(e) => upd(i, { amt: e.target.value })} />
                            <label className="mt-0.5 flex items-center gap-1 text-xs">
                              <input type="checkbox" checked={l.vat} onChange={(e) => upd(i, { vat: e.target.checked, amt: '' })} />부가세 별도
                            </label>
                          </>
                        )}
                      </td>
                      <td className={`${td} min-w-[14rem]`}>
                        {l.allocId
                          ? <span>{l.allocLabel ?? `편성 NO ${l.allocId}`}{l.allocSug && <span className="ml-1 rounded bg-amber-100 px-1 text-xs text-amber-800">추천</span>}</span>
                          : <span className="text-amber-700">편성을 고르세요</span>}
                        <button type="button" className="btn-ghost ml-1 py-0 text-xs" onClick={() => upd(i, { open: !l.open })}>{l.open ? '닫기' : '바꾸기'}</button>
                      </td>
                      <td className={`${td} whitespace-nowrap`}>
                        <select className={`field h-8 w-36 py-0 ${!l.account ? 'border-amber-400' : ''}`} value={l.account} onChange={(e) => upd(i, { account: e.target.value })}>
                          <option value="">계정 고르기</option>
                          {l.account && !hints.deductAccounts.includes(l.account) && <option value={l.account}>{l.account}</option>}
                          {hints.deductAccounts.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                        {!l.row.deduct_account && l.row.sug_account && l.account === l.row.sug_account && <span className="ml-1 rounded bg-amber-100 px-1 text-xs text-amber-800">추천</span>}
                      </td>
                      <td className={td}>
                        {l.row.plan_n > 0 ? <span className="text-xs">분납 정산월대로</span> : (
                          <select className="field h-8 w-36 py-0" value={mv} onChange={(e) => pickMon(i, e.target.value)}>
                            <option value="">정산월 없음</option>
                            {monOpts.map((m) => <option key={m} value={m}>{monLabel(m)}{hints.finishedMons.includes(m) ? ' (마감)' : ''}</option>)}
                          </select>
                        )}
                      </td>
                    </tr>
                    {l.open && (
                      <tr className="border-b bg-canvas/60">
                        <td />
                        <td className="px-2 py-2" colSpan={6}>
                          <AllocPicker value={l.allocId} label={l.allocLabel} initialTerm={l.row.plate}
                                       onPick={(id) => upd(i, { allocId: id, allocSug: false })}
                                       onPickRow={(r) => upd(i, { allocLabel: [r.car_no, r.owner_name, r.driver_name].filter(Boolean).join(' · '), open: false })} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </>
      )}
      {lines && lines.length === 0 && <p className="text-ink-soft">공제할 수 있는 줄이 없습니다.</p>}
      {skipped.length > 0 && (
        <p className="text-xs text-ink-soft">
          빠진 줄 {skipped.length}개 — {skipped.slice(0, 5).map((x) => `${x.row.pay_date.slice(5)} ${x.row.vendor ?? ''}(${x.why})`).join(', ')}{skipped.length > 5 ? ' …' : ''}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        {missing > 0 && <span className="text-amber-700">편성·계정이 빈 줄 {missing}개</span>}
        <button className="btn" onClick={onClose} disabled={busy}>취소</button>
        <button className="btn-primary" disabled={busy || !lines?.length || missing > 0} onClick={() => void commit()}>
          {busy ? '넣는 중…' : `${lines?.length ?? 0}줄 ${open ? '재무추가에 공제' : '공제 대상으로 저장'}`}
        </button>
      </div>
    </div>
  );
}

function DeductCheckPanel({ from, to, corpId, onClose, onChanged }: {
  from: string; to: string; corpId: number | null; onClose: () => void; onChanged: () => void;
}) {
  const { me } = useMe();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.cashBookDeductCheck>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);
  const [arm, setArm] = useState<'commit' | 'switch' | null>(null);
  async function load() {
    setBusy(true); setError(null);
    try { setData(await api.cashBookDeductCheck({ dateFrom: from, dateTo: to, corpId })); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  useEffect(() => { void load(); }, [from, to, corpId]);
  const ready = (data?.problems ?? []).filter((p) => p.kind === 'pending' && p.ready);
  async function toggle() {
    if (!data) return;
    if (arm !== 'switch') { setArm('switch'); return; }
    setArm(null); setBusy(true); setError(null);
    try { await api.setCashBookDirectDeduct({ open: !data.open }); await load(); } catch (e) { setError(errMsg(e)); setBusy(false); }
  }
  async function commit() {
    if (arm !== 'commit') { setArm('commit'); return; }
    setArm(null); setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.commitCashBookDeduct({ ids: ready.map((p) => Number(p.cash_book_id)) });
      setNotice(`재무추가에 넣었습니다 — 출납 ${r.rows}줄 → 재무추가 ${r.created}줄 · ${Number(r.amount).toLocaleString()}원`);
      onChanged(); await load();
    } catch (e) { setError(errMsg(e)); setBusy(false); }
  }
  const KIND = { pending: '안 넣음', deleted: '재무추가에서 지워짐', changed: '재무추가 금액 바뀜' } as const;
  return (
    <div className="card max-h-[90vh] space-y-3 overflow-y-auto p-5 text-sm">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-bold">재무추가 공제·대조 <span className="font-normal text-ink-soft">{from} ~ {to}</span></h2>
        {me?.role === 'admin' && data && (
          <button className={`btn text-xs ${arm === 'switch' ? 'border-red-400 text-red-700' : ''}`} disabled={busy}
            onClick={() => void toggle()} onBlur={() => setArm(null)}
            title="버스인웹 전환(전량 재적재) 뒤에 켭니다 — 그 전에 넣으면 재적재가 재무추가 줄을 지웁니다">
            {arm === 'switch' ? (data.open ? '정말 끌까요? 한 번 더' : '정말 켤까요? 한 번 더') : `바로 공제: ${data.open ? '켜짐' : '꺼짐'}`}
          </button>
        )}
      </div>
      <p className="text-ink-soft">
        날짜·법인마다 출납일보 <b>공제액 합계</b>와 거기서 넣은 <b>재무추가 공제 합계</b>(지금 값)를 맞춰 봅니다. 차이가 있으면 아래 줄 목록에 이유가 나옵니다.
        「직접」은 그날 출납일보와 상관없이 재무추가에 넣은 공제(그 법인 소속)로, 손으로 넣었다면 여기와 겹칠 수 있습니다.
      </p>
      {data && !data.open && <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-800">바로 공제는 <b>꺼져 있습니다</b> — 버스인웹 전환 뒤 관리자가 켭니다. 대조는 지금도 봅니다.</p>}
      {error && <ErrorBox message={error} />}
      {data && (
        <div className="max-h-72 overflow-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b text-left text-ink-soft">
              <th className="px-2 py-1">지급일</th><th className="px-2">법인</th><th className="px-2 text-right">출납일보 공제</th><th className="px-2 text-right">재무추가</th>
              <th className="px-2 text-right">차이</th><th className="px-2">안 넣음</th><th className="px-2">손으로 완료</th><th className="px-2">어긋남</th><th className="px-2 text-right">직접</th>
            </tr></thead>
            <tbody>
              {data.days.map((d) => {
                const diff = Number(d.cash) - Number(d.linked);
                const bad = d.broken_n > 0 || (diff !== 0 && d.pending_n + d.manual_n === 0);
                return (
                  <tr key={`${d.pay_date}-${d.corp_id}`} className={`border-b ${bad ? 'bg-red-50 text-red-800' : diff === 0 ? '' : 'text-amber-800'}`}>
                    <td className="px-2 py-1">{dayLabel(d.pay_date)}</td><td className="px-2">{d.corp_name}</td>
                    <td className="px-2 text-right">{money(d.cash)}</td><td className="px-2 text-right">{money(d.linked)}</td>
                    <td className="px-2 text-right font-medium">{diff === 0 ? '✓' : money(String(diff))}</td>
                    <td className="px-2">{d.pending_n ? `${d.pending_n}줄 ${money(d.pending)}` : ''}</td>
                    <td className="px-2" title="공제 완료로 표시했지만 여기서 넣지 않은 줄(전환 전 버스인에 넣은 것 등)">{d.manual_n || ''}</td>
                    <td className="px-2">{d.broken_n || ''}</td>
                    <td className="px-2 text-right">{d.direct_n ? `${d.direct_n}건 ${money(d.direct)}` : ''}</td>
                  </tr>
                );
              })}
              {data.days.length === 0 && <tr><td className="px-2 py-2 text-ink-faint" colSpan={9}>이 기간에 공제 대상 줄이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {data && data.problems.length > 0 && (
        <div className="space-y-1">
          <div className="font-medium">확인할 줄 {data.problems.length}</div>
          <ul className="max-h-48 overflow-auto rounded-lg border text-xs">
            {data.problems.map((p) => (
              <li key={`${p.cash_book_id}-${p.drive_mon ?? ''}`} className="flex gap-2 border-b px-2 py-1">
                <span className={p.kind === 'pending' ? 'text-amber-700' : 'text-red-700'}>{KIND[p.kind]}</span>
                <span>{p.pay_date} {p.corp_name} · {dash(p.vendor)} · {dash(p.summary)} · {money(p.deduct_amount)}</span>
                {p.drive_mon && <span className="text-ink-soft">정산월 {p.drive_mon} · 재무추가 NO {p.result_daily_id}</span>}
                {p.kind === 'pending' && !p.ready && <span className="text-ink-faint">편성·계정 먼저</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>닫기</button>
        <button className={`btn-primary ${arm === 'commit' ? 'ring-2 ring-red-400' : ''}`} disabled={busy || !data?.open || ready.length === 0}
          onClick={() => void commit()} onBlur={() => setArm(null)}
          title={data?.open ? '편성·계정을 정한 안 넣은 줄을 재무추가에 넣습니다 — 전부 아니면 전무' : '바로 공제가 꺼져 있습니다'}>
          {arm === 'commit' ? `${ready.length}줄 넣기 — 한 번 더` : `재무추가에 넣기(${ready.length})`}
        </button>
      </div>
    </div>
  );
}

/**
 * B/ 계정 재무추가 미공제 확인(#459 정여진) — 「B/OOO」 구분 줄 중 재무추가에 안 들어간 것.
 * 과태료(범칙금·통행료)는 과태료 화면이 공제하므로 서버가 뺀다. 사유를 적으면 그 줄은 다시 안 뜬다.
 */
function BPendingPanel({ corpId, rows, skipped, from, canWrite, onClose, onOpen, onChanged }: {
  corpId: number | null; rows: CashBookRow[]; skipped: CashBookRow[]; from: string; canWrite: boolean;
  onClose: () => void; onOpen: (r: CashBookRow) => void; onChanged: () => void;
}) {
  const [pick, setPick] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const all = rows.length > 0 && pick.size === rows.length;
  // 사유 적은 줄(같은 기간·법인) — 사유 고치기 · 지우기(빈 사유 = 다시 미공제 목록으로). 줄별 입력값은 id → 사유.
  const [showSkipped, setShowSkipped] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});
  async function saveSkip(id: string, next: string) {
    setBusy(true); setError(null);
    try {
      await api.setCashBookBSkip({ ids: [Number(id)], reason: next.trim() });
      setEdits((e) => { const n = { ...e }; delete n[id]; return n; }); onChanged();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  async function save() {
    if (!reason.trim() || pick.size === 0) return;
    setBusy(true); setError(null);
    try {
      await api.setCashBookBSkip({ ids: [...pick].map(Number), reason: reason.trim() });
      setPick(new Set()); setReason(''); onChanged();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  return (
    <div className="card max-h-[90vh] space-y-3 overflow-y-auto p-5 text-sm">
      <h2 className="font-bold">재무추가 공제 안 된 B/ 계정 <span className="font-normal text-ink-soft">{from} 부터 · {rows.length}줄{corpId ? '' : ' · 전체 법인'}</span></h2>
      <p className="text-ink-soft">
        구분이 「B/」로 시작하는데 재무추가에 공제가 안 들어간 줄입니다. 범칙금·통행료는 과태료 화면에서 공제하므로 빠져 있습니다.
        줄을 두 번 누르면 고치는 창이 열립니다. 재무추가에 직접 넣었거나 공제할 줄이 아니면 골라서 사유를 적어 주세요 — 사유를 적은 줄은 여기에 다시 나오지 않습니다.
      </p>
      {error && <ErrorBox message={error} />}
      <div className="max-h-[55vh] overflow-auto rounded-lg border">
        <table className="w-full whitespace-nowrap text-xs">
          <thead><tr className="border-b text-left text-ink-soft">
            <th className="px-2 py-1">{canWrite && <input type="checkbox" checked={all} onChange={() => setPick(all ? new Set() : new Set(rows.map((r) => String(r.cash_book_id))))} />}</th>
            <th className="px-2">지급일</th><th className="px-2">법인</th><th className="px-2">구분</th><th className="px-2">시재계정</th>
            <th className="px-2">업체(기사)</th><th className="px-2">적요</th><th className="px-2 text-right">금액</th><th className="px-2">상태</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const id = String(r.cash_book_id);
              return (
                <tr key={id} className="cursor-pointer border-b hover:bg-canvas" onDoubleClick={() => onOpen(r)}>
                  <td className="px-2 py-1" data-nocopy>{canWrite && (
                    <input type="checkbox" checked={pick.has(id)}
                           onChange={() => setPick((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; })} />)}</td>
                  <td className="px-2">{r.pay_date}</td><td className="px-2">{r.corp_name}</td><td className="px-2">{dash(r.method)}</td>
                  <td className="px-2">{dash(r.cash_account)}</td><td className="px-2">{dash(r.vendor)}</td>
                  <td className="max-w-[22rem] truncate px-2" title={r.summary ?? ''}>{dash(r.summary)}</td>
                  <td className="px-2 text-right tabular-nums">{money(r.amount)}</td>
                  <td className="px-2">{r.deduct
                    ? <span className="text-amber-700">공제 대상 · {!r.car_alloc_id ? '편성 없음' : !r.deduct_account ? '계정 없음' : '안 넣음'}</span>
                    : <span className="text-ink-soft">공제 대상 아님</span>}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td className="px-2 py-3 text-ink-faint" colSpan={9}>확인할 줄이 없습니다.</td></tr>}
          </tbody>
        </table>
      </div>
      {showSkipped && (
        <div className="space-y-1">
          <p className="text-xs text-ink-soft">사유를 적은 줄입니다({from} 부터). 출납일보 목록의 공제 칸에도 「미공제 사유」로 보입니다. 사유를 지우면 위 미공제 목록으로 돌아갑니다.</p>
          <div className="max-h-[40vh] overflow-auto rounded-lg border">
            <table className="w-full whitespace-nowrap text-xs">
              <thead><tr className="border-b text-left text-ink-soft">
                <th className="px-2 py-1">지급일</th><th className="px-2">법인</th><th className="px-2">구분</th>
                <th className="px-2">업체(기사)</th><th className="px-2">적요</th><th className="px-2 text-right">금액</th>
                <th className="px-2">사유</th><th className="px-2">적은 사람·시각</th><th className="px-2" />
              </tr></thead>
              <tbody>
                {skipped.map((r) => {
                  const id = String(r.cash_book_id);
                  const v = edits[id] ?? r.b_skip_reason ?? '';
                  const changed = v.trim() !== (r.b_skip_reason ?? '') && v.trim() !== '';
                  return (
                    <tr key={id} className="border-b hover:bg-canvas" onDoubleClick={() => onOpen(r)}>
                      <td className="px-2 py-1">{r.pay_date}</td><td className="px-2">{r.corp_name}</td><td className="px-2">{dash(r.method)}</td>
                      <td className="px-2">{dash(r.vendor)}</td>
                      <td className="max-w-[16rem] truncate px-2" title={r.summary ?? ''}>{dash(r.summary)}</td>
                      <td className="px-2 text-right tabular-nums">{money(r.amount)}</td>
                      <td className="px-2" onDoubleClick={(e) => e.stopPropagation()}>{canWrite
                        ? <input className="field w-56 py-0.5 text-xs" maxLength={300} value={v}
                                 onChange={(e) => setEdits((x) => ({ ...x, [id]: e.target.value }))}
                                 onKeyDown={(e) => { if (e.key === 'Enter' && changed) void saveSkip(id, v); }} />
                        : r.b_skip_reason}</td>
                      <td className="px-2 text-ink-faint">{r.b_skip_by} · {r.b_skip_at}</td>
                      <td className="px-2" onDoubleClick={(e) => e.stopPropagation()}>{canWrite && (
                        <span className="flex gap-1">
                          <button className="btn-primary px-2 py-0.5 text-xs" disabled={busy || !changed} onClick={() => void saveSkip(id, v)}>고치기</button>
                          <button className="btn-ghost px-2 py-0.5 text-xs" disabled={busy} title="사유를 지우고 미공제 목록으로 되돌립니다"
                                  onClick={() => void saveSkip(id, '')}>지우기</button>
                        </span>)}</td>
                    </tr>
                  );
                })}
                {skipped.length === 0 && <tr><td className="px-2 py-3 text-ink-faint" colSpan={9}>사유를 적은 줄이 없습니다.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button className="btn-ghost mr-auto" onClick={() => setShowSkipped((x) => !x)}
                title="사유를 적은 줄을 보고 사유를 고치거나 지웁니다(지우면 다시 미공제 목록으로)">
          {showSkipped ? '사유 적은 줄 닫기' : `사유 적은 줄 보기(${skipped.length})`}
        </button>
        {canWrite && (
          <>
            <input className="field w-80" placeholder="사유 (예: 재무추가에 직접 등록)" maxLength={300} value={reason}
                   onChange={(e) => setReason(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void save(); }} />
            <button className="btn-primary" disabled={busy || !reason.trim() || pick.size === 0} onClick={() => void save()}>
              사유 저장{pick.size ? ` (${pick.size}줄)` : ''}
            </button>
          </>
        )}
        <button className="btn-ghost" onClick={onClose}>닫기</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 매월 반복 출금(#19 4번)
// ─────────────────────────────────────────────────────────────────────────────
export function CashRulesTab({ corps }: { corps: AcctCorp[] }) {
  const fitRef = useRef<HTMLDivElement>(null);
  const fitH = useFitHeight(fitRef);
  const [corpId, setCorpId] = useAcctCorp();
  const [rows, setRows] = useState<CashRuleRow[]>([]);
  const [nonce, setNonce] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<CashRuleRow | 'new' | null>(null);
  const [month, setMonth] = useState(nextMonth(monthOf(kst())));
  const [plan, setPlan] = useState<{ items: CashRulePlanItem[]; create: number; skip: number } | null>(null);
  // #447 후속 — 미리보기 줄마다 고친 적요(규칙 번호 → 적요). 이 달 출납일보 줄에만 들어가고 규칙은 그대로.
  const [sumEdits, setSumEdits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);

  // #114 — 고른 규칙(건별·전체) 지우기. 법인을 바꾸거나 다시 불러오면 고름을 비운다.
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [confirmDel, setConfirmDel] = useState(false);
  useEffect(() => {
    setPicked(new Set()); setConfirmDel(false);
    api.listCashRules({ corpId }).then((r) => setRows(r.rows)).catch((e) => setError(errMsg(e)));
  }, [corpId, nonce]);
  useEffect(() => { setPlan(null); }, [month, nonce]);
  const [bulk, setBulk] = useState(false);
  const allPicked = rows.length > 0 && rows.every((r) => picked.has(Number(r.rule_id)));
  const togglePick = (id: number) => {
    setConfirmDel(false);
    setPicked((cur) => { const s = new Set(cur); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  };
  async function removePicked() {
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await api.deleteCashRules({ ids: [...picked] });
      setNotice(`반복 출금 규칙 ${r.deleted}개를 지웠습니다. 이미 채운 출납일보 줄은 그대로입니다.`);
      setNonce((n) => n + 1);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); setConfirmDel(false); }
  }

  async function preview() {
    setBusy(true); setError(null); setNotice(null);
    try { setPlan(await api.previewCashRules({ month })); setSumEdits({}); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  async function commit() {
    setBusy(true); setError(null);
    try {
      const summaries = Object.fromEntries(Object.entries(sumEdits).filter(([id, v]) =>
        v !== (plan?.items.find((i) => String(i.rule_id) === id)?.summary ?? '')));
      const r = await api.commitCashRules({ month, summaries });
      setNotice(`${r.month} 출납일보에 ${r.created}줄을 만들었습니다.`);
      setPlan(null); setNonce((n) => n + 1);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-2">
      <div className="card space-y-1.5 px-3 py-2 text-sm">
        <p className="page-hint">
          반복해서 나가는 출금(할부 자동이체·임차료·보험료·자동차세 …)을 규칙으로 등록해 두면, 달마다 <b>한 번 눌러</b> 출납일보에 채웁니다.
          같은 달을 두 번 눌러도 <b>한 번만</b> 들어가고, 출납일보에서 지운 줄은 다시 만들지 않습니다.
          규칙을 지우거나 종료월을 넣어도 <b>이미 채운 출납일보 줄은 그대로</b>이고, 그 뒤 달부터 만들지 않습니다.
        </p>
        <div className="btn-row-sm flex flex-wrap items-center gap-2">
          <input type="month" className="field w-40" value={month} onChange={(e) => setMonth(e.target.value)} />
          <button className="btn" onClick={preview} disabled={busy}>{busy && !plan ? '확인 중…' : '이 달 채울 것 보기'}</button>
          {plan && (
            <button className="btn-primary" onClick={commit} disabled={busy || plan.create === 0}>
              {busy ? '만드는 중…' : `${plan.create}줄 만들기`}
            </button>
          )}
        </div>
        {plan && plan.create > 0 && (
          <p className="text-xs text-ink-soft">적요는 만들기 전에 줄마다 고칠 수 있습니다 — 이 달 출납일보 줄에만 들어가고 규칙은 그대로입니다.</p>
        )}
        {plan && (
          <div className="max-h-64 overflow-auto rounded border">
            {plan.items.length === 0 && <p className="p-2 text-ink-faint">이 달에 해당하는 규칙이 없습니다.</p>}
            <table className="w-full text-xs">
              <tbody>
                {plan.items.map((i) => (
                  <tr key={i.rule_id} className={`border-b ${i.exists ? 'text-ink-faint' : ''}`}>
                    <td className="px-2 py-1">{i.pay_date}{i.base_date && i.base_date !== i.pay_date && <span className="ml-1 text-ink-faint">({i.base_date.slice(5)} 휴일)</span>}</td><td className="px-2">{i.corp_name}</td><td className="px-2">{dash(i.vendor)}</td>
                    <td className="px-2">
                      {i.exists ? dash(i.summary) : (
                        <input className="field w-full min-w-[16rem] py-0.5 text-xs" maxLength={300} title="이 달 줄에만 들어갑니다(규칙은 그대로)"
                               value={sumEdits[String(i.rule_id)] ?? i.summary ?? ''}
                               onChange={(e) => setSumEdits((cur) => ({ ...cur, [String(i.rule_id)]: e.target.value }))} />
                      )}
                    </td><td className="px-2 text-right">{money(i.amount)}</td>
                    <td className="px-2">{i.exists ? '이미 만듦' : '새로'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="btn-row-sm flex items-center gap-2">
        <CorpSelect corps={corps} value={corpId} onChange={setCorpId} />
        {picked.size > 0 && (confirmDel ? (
          <>
            <span className="text-sm text-red-700">고른 {picked.size}개를 지울까요?</span>
            <button className="btn-primary bg-red-600" disabled={busy} onClick={() => void removePicked()}>{busy ? '지우는 중…' : `${picked.size}개 지우기`}</button>
            <button className="btn-ghost" disabled={busy} onClick={() => setConfirmDel(false)}>그만두기</button>
          </>
        ) : (
          <>
            <button className="btn text-red-600" onClick={() => setConfirmDel(true)}>고른 {picked.size}개 지우기</button>
            <button className="btn-ghost text-xs" onClick={() => setPicked(new Set())}>고름 풀기</button>
          </>
        ))}
        <button className="btn-ghost ml-auto" onClick={() => void downloadRuleTemplate(corps)}>양식 받기</button>
        <button className="btn" onClick={() => setBulk(true)}>엑셀로 여러 개 추가</button>
        <button className="btn-primary" onClick={() => setEditing('new')}>규칙 추가</button>
      </div>
      {error && <ErrorBox message={error} />}
      <TopScrollbar target={fitRef} />
      <div ref={fitRef} className="overflow-auto" style={{ maxHeight: fitH }}>
        <table className="w-full min-w-[1000px] text-sm [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-surface">
          <thead>
            <tr className="border-b text-left text-ink-soft">
              <th className="w-8 px-2 py-2">
                <input type="checkbox" aria-label="전체 고르기" checked={allPicked} disabled={rows.length === 0}
                       onChange={() => { setConfirmDel(false); setPicked(allPicked ? new Set() : new Set(rows.map((r) => Number(r.rule_id)))); }} />
              </th>
              <th className="px-2 py-2">법인</th><th className="px-2">반복</th><th className="px-2">구분</th><th className="px-2">시재계정</th>
              <th className="px-2">업체(기사)</th><th className="px-2">적요</th><th className="px-2 text-right">금액</th>
              <th className="px-2">기간</th><th className="px-2">사용</th><th className="px-2">마지막 생성</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={11} className="px-2 py-3 text-ink-faint">등록한 규칙이 없습니다.</td></tr>}
            {rows.map((r) => (
              <tr key={r.rule_id} onDoubleClick={() => setEditing(r)} title="두 번 누르면 고치기"
                  className={`cursor-pointer border-b hover:bg-brand-50/40 ${r.deduct ? 'text-blue-700' : ''} ${r.is_active ? '' : 'opacity-50'} ${picked.has(Number(r.rule_id)) ? 'bg-red-50' : ''}`}>
                <td className="px-2" data-nocopy="1" onDoubleClick={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); togglePick(Number(r.rule_id)); }}>
                  <input type="checkbox" aria-label="고르기" checked={picked.has(Number(r.rule_id))} readOnly className="pointer-events-none" />
                </td>
                <td className="px-2 py-1.5">{r.corp_name}</td><td className="whitespace-nowrap px-2">{repeatLabel(r)}</td>
                <td className="px-2">{dash(r.method)}</td><td className="px-2">{dash(r.cash_account)}</td>
                <td className="px-2">{dash(r.vendor)}</td><td className="px-2">{dash(r.summary)}</td>
                <td className="px-2 text-right">{money(r.amount)}</td>
                <td className="px-2 text-xs">{r.start_month} ~ {r.end_month ?? ''}</td>
                <td className="px-2">{r.is_active ? '사용' : '멈춤'}</td>
                <td className="px-2 text-xs">{dash(r.last_month)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <Modal width="max-w-2xl">
          <RuleForm corps={corps} row={editing === 'new' ? null : editing}
                    defaultCorp={corpId ?? corps.find((c) => !c.parent_corp_id)?.corp_id ?? 0}
                    onClose={() => setEditing(null)} onSaved={() => { setEditing(null); setNonce((n) => n + 1); }} />
        </Modal>
      )}
      {bulk && (
        <Modal width="max-w-4xl">
          <RuleBulkForm onClose={() => setBulk(false)} onSaved={(n) => { setBulk(false); setNotice(`반복 출금 규칙 ${n}개를 등록했습니다.`); setNonce((x) => x + 1); }} />
        </Modal>
      )}
    </div>
  );
}

// ─── 반복 주기(#39) ─────────────────────────────────────────────────────────
const WEEKS = ['첫째', '둘째', '셋째', '넷째', '마지막'];

/** 「매월 25일」「매월 둘째 화요일」 + 나가는 달(「· 6·12월」). */
function repeatLabel(r: Pick<CashRuleRow, 'cycle' | 'day_of_month' | 'week_of_month' | 'weekday' | 'months'>): string {
  const when = r.cycle === 'weekday'
    ? `${WEEKS[(r.week_of_month ?? 1) - 1]} ${WD[r.weekday ?? 0]}요일`
    : `${r.day_of_month}일`;
  return r.months ? `${r.months.join('·')}월 ${when}` : `매월 ${when}`;
}

type MonthsPreset = 'every' | 'quarter' | 'half' | 'year' | 'custom';
/** 시작월 기준으로 펼친다(10월 시작 분기 = 1·4·7·10월) — 서버 엑셀 해석(`cashRuleParse.parseMonths`)과 같은 규칙. */
function monthsOf(preset: MonthsPreset, startMonth: string, custom: number[]): number[] | null {
  const step = preset === 'quarter' ? 3 : preset === 'half' ? 6 : preset === 'year' ? 12 : 0;
  if (preset === 'every') return null;
  if (preset === 'custom') return custom.length === 0 || custom.length === 12 ? null : [...custom].sort((a, b) => a - b);
  const m0 = Number(startMonth.slice(5, 7)) || 1;
  const out: number[] = [];
  for (let k = 0; k < 12; k += step) out.push(((m0 - 1 + k) % 12) + 1);
  return out.sort((a, b) => a - b);
}
function presetOf(months: number[] | null, startMonth: string): MonthsPreset {
  if (!months) return 'every';
  for (const p of ['quarter', 'half', 'year'] as const) {
    if (JSON.stringify(monthsOf(p, startMonth, [])) === JSON.stringify(months)) return p;
  }
  return 'custom';
}

const RULE_TEMPLATE = ['법인', '반복', '나가는 달', '시작월', '종료월', '구분', '시재계정', '업체(기사)', '적요', '금액', '증빙', 'memo'];

/** 대량 등록 양식 — 안내 줄 + 머리줄 + 예시 3줄. 서버는 「법인·반복」 머리줄을 찾아 그 아래만 읽는다. */
async function downloadRuleTemplate(corps: AcctCorp[]) {
  const XLSX = await import('xlsx');
  const corp = corps.find((c) => !c.parent_corp_id)?.short_name ?? '파주';
  const start = nextMonth(monthOf(kst()));
  const aoa = [
    [`※ 예시 줄은 지우고 적어 주세요. 법인 = ${corps.map((c) => c.short_name).join(' · ')}`],
    ['※ 반복 = 매월 25일 · 말일 · 둘째 화요일 · 마지막 금요일   나가는 달 = 비우면 매월 · 분기 · 반기 · 매년 · 6월,12월   시작월·종료월 = 2026-10 (종료월 비우면 계속)   증빙 = O / X'],
    RULE_TEMPLATE,
    [corp, '매월 25일', '', start, '', '자동이체', '', '○○캐피탈', '차량 할부', '1,250,000', 'X', ''],
    [corp, '마지막 금요일', '', start, '', '자동이체', '', '○○화재', '보험료', '300,000', '', ''],
    [corp, '매월 30일', '6월,12월', start, '', '자동이체', '', '○○구청', '자동차세', '520,000', '', ''],
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [8, 14, 12, 9, 9, 10, 10, 14, 18, 12, 6, 16].map((wch) => ({ wch }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '반복출금');
  XLSX.writeFile(wb, '반복출금_대량등록_양식.xlsx');
}

/** 엑셀 칸 → 문자열. 날짜로 바뀐 칸(시작월 등)은 YYYY-MM 으로 되돌린다. */
function cellText(v: unknown): string {
  if (v instanceof Date) return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}`;
  return String(v ?? '').trim();
}

function RuleBulkForm({ onClose, onSaved }: { onClose: () => void; onSaved: (n: number) => void }) {
  const [rows, setRows] = useState<string[][] | null>(null);
  const [res, setRes] = useState<Awaited<ReturnType<typeof api.importCashRules>> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('파일 읽는 중…'); setError(null); setRes(null);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]!]!;
      const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: '', raw: true, blankrows: false })
        .map((r) => r.slice(0, 12).map(cellText));
      setRows(aoa);
      setBusy('확인 중…');
      setRes(await api.importCashRules({ rows: aoa }));
    } catch (err) { setError(errMsg(err)); setRows(null); } finally { setBusy(null); e.target.value = ''; }
  }
  async function commit() {
    if (!rows) return;
    setBusy('등록 중…'); setError(null);
    try {
      const r = await api.importCashRules({ rows, commit: true });
      if (r.committed > 0) onSaved(r.committed); else setRes(r);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(null); }
  }

  return (
    <div className="card max-h-[90vh] space-y-3 overflow-y-auto p-5 text-sm">
      <h2 className="font-semibold">반복 출금 엑셀로 여러 개 추가</h2>
      <p className="text-ink-soft">「양식 받기」로 받은 파일에 적어 올리면 먼저 확인하고, 틀린 줄이 하나도 없을 때만 등록합니다.</p>
      <input type="file" accept=".xlsx,.xls" onChange={pick} disabled={busy !== null} />
      {busy && <p className="text-ink-faint">{busy}</p>}
      {error && <ErrorBox message={error} />}
      {res && res.errors.length > 0 && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-red-800">
          <b>고칠 줄 {res.errors.length}개</b> — 고친 뒤 다시 올려 주세요.
          <ul className="mt-1 list-disc pl-5">{res.errors.slice(0, 20).map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}
      {res && res.items.length > 0 && (
        <div className="max-h-72 overflow-auto rounded border">
          <table className="w-full text-xs">
            <thead><tr className="border-b text-left text-ink-soft">
              <th className="px-2 py-1">줄</th><th className="px-2">법인</th><th className="px-2">반복</th><th className="px-2">나가는 달</th>
              <th className="px-2">기간</th><th className="px-2">업체</th><th className="px-2">적요</th><th className="px-2 text-right">금액</th>
            </tr></thead>
            <tbody>
              {res.items.map((i) => (
                <tr key={i.line} className="border-b">
                  <td className="px-2 py-1">{i.line}</td><td className="px-2">{i.corp}</td><td className="px-2">{i.repeat}</td>
                  <td className="px-2">{i.months ? `${i.months.join('·')}월` : '매월'}</td>
                  <td className="px-2">{i.startMonth} ~ {i.endMonth ?? ''}</td><td className="px-2">{dash(i.vendor)}</td>
                  <td className="px-2">{dash(i.summary)}</td><td className="px-2 text-right">{money(i.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex gap-2">
        <button className="btn-primary" onClick={commit} disabled={busy !== null || !res || res.errors.length > 0 || res.items.length === 0}>
          {res && res.errors.length === 0 ? `${res.items.length}개 등록` : '등록'}
        </button>
        <button className="btn-ghost" onClick={onClose}>닫기</button>
      </div>
    </div>
  );
}

function RuleForm({ corps, row, defaultCorp, onClose, onSaved }: {
  corps: AcctCorp[]; row: CashRuleRow | null; defaultCorp: number; onClose: () => void; onSaved: () => void;
}) {
  const [day, setDay] = useState(String(row?.day_of_month ?? 5));
  const [cycle, setCycle] = useState<'day' | 'weekday'>(row?.cycle ?? 'day');
  const [week, setWeek] = useState(row?.week_of_month ?? 1);
  const [weekday, setWeekday] = useState(row?.weekday ?? 1);
  const [startMonth, setStartMonth] = useState(row?.start_month ?? nextMonth(monthOf(kst())));
  const [preset, setPreset] = useState<MonthsPreset>(presetOf(row?.months ?? null, row?.start_month ?? nextMonth(monthOf(kst()))));
  const [custom, setCustom] = useState<number[]>(row?.months ?? []);
  const months = monthsOf(preset, startMonth, custom);
  const [endMonth, setEndMonth] = useState(row?.end_month ?? '');
  const [isActive, setIsActive] = useState(row?.is_active ?? true);
  const [f, setF] = useState<FormState>(row ? {
    ...blank(row.corp_id), method: row.method ?? '', cashAccount: row.cash_account ?? '', vendor: row.vendor ?? '', summary: row.summary ?? '',
    amount: Number(row.amount).toLocaleString(), evidence: row.evidence === null ? '' : row.evidence ? 'O' : 'X', memo: row.memo ?? '',
    deduct: row.deduct, vatSeparate: row.vat_separate,
  } : blank(defaultCorp));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  // #447 — 적요가 적은 그대로 들어가는 달(서버 saveCashRule·RULE_BASE_SQL 과 같은 판정): 새 규칙 = 시작월 ·
  // 적요를 고치면 아직 안 만든 첫 달 · 안 고치면 원래 기준 달(시작월보다 이르면 시작월).
  const later = (a: string, b: string | null | undefined) => (b && b > a ? b : a);
  const summaryBase = !row ? startMonth
    : f.summary.trim() !== (row.summary ?? '').trim() ? later(startMonth, row.last_month ? nextMonth(row.last_month) : null)
      : later(startMonth, row.summary_base);
  const monthLabel = (m: string) => `${m.slice(0, 4)}년 ${Number(m.slice(5, 7))}월`;

  async function save(remove = false) {
    setBusy(true); setError(null);
    try {
      await api.saveCashRule(remove && row ? { id: row.rule_id, remove: true, ...toInput(f) }
        : { id: row?.rule_id, cycle, dayOfMonth: cycle === 'day' ? Number(day) : undefined,
            weekOfMonth: cycle === 'weekday' ? week : null, weekday: cycle === 'weekday' ? weekday : null, months,
            startMonth, endMonth: endMonth || null, isActive, ...toInput(f), deductAmount: null, deductPlan: null });
      onSaved();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }

  return (
    <div className="card max-h-[90vh] space-y-4 overflow-y-auto p-5">
      <h2 className="font-semibold">{row ? '반복 출금 고치기' : '반복 출금 추가'}</h2>
      <div className="grid grid-cols-4 gap-3 text-sm">
        <label className="space-y-1">반복
          <select className="field w-full" value={cycle} onChange={(e) => setCycle(e.target.value as 'day' | 'weekday')}>
            <option value="day">매월 며칠</option><option value="weekday">매월 몇째 요일</option>
          </select>
        </label>
        {cycle === 'day' ? (
          <label className="space-y-1">며칠<input className="field w-full" type="number" min={1} max={31} value={day} onChange={(e) => setDay(e.target.value)} /></label>
        ) : (
          <div className="space-y-1">몇째 요일
            <div className="flex gap-1">
              <select className="field w-full" value={week} onChange={(e) => setWeek(Number(e.target.value))} aria-label="몇째 주">
                {WEEKS.map((w, i) => <option key={w} value={i + 1}>{w}</option>)}
              </select>
              <select className="field w-full" value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} aria-label="요일">
                {WD.map((w, i) => <option key={w} value={i}>{w}요일</option>)}
              </select>
            </div>
          </div>
        )}
        <label className="space-y-1">나가는 달
          <select className="field w-full" value={preset} onChange={(e) => setPreset(e.target.value as MonthsPreset)}>
            <option value="every">매월</option><option value="quarter">분기(3개월마다)</option><option value="half">반기(6개월마다)</option>
            <option value="year">매년(1회)</option><option value="custom">직접 고르기</option>
          </select>
        </label>
        <p className="self-end pb-2 text-xs text-ink-soft">{months ? `${months.join('·')}월에 나갑니다` : '매월 나갑니다'}</p>
      </div>
      {preset === 'custom' && (
        <div className="flex flex-wrap gap-1 text-sm">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <button key={m} type="button" onClick={() => setCustom((c) => (c.includes(m) ? c.filter((x) => x !== m) : [...c, m]))}
                    className={`rounded-lg border px-2.5 py-1 ${custom.includes(m) ? 'border-brand-600 bg-brand-50 text-brand-700' : 'text-ink-soft'}`}>
              {m}월
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-4 gap-3 text-sm">
        <label className="space-y-1">시작월<input className="field w-full" type="month" value={startMonth} onChange={(e) => setStartMonth(e.target.value)} /></label>
        <label className="space-y-1">종료월(없으면 계속)<input className="field w-full" type="month" value={endMonth} onChange={(e) => setEndMonth(e.target.value)} /></label>
        <label className="flex items-end gap-2 pb-2"><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> 사용</label>
      </div>
      <p className="text-xs text-ink-faint">
        그 달에 없는 날(예: 31일)은 말일로 당깁니다. 분기·반기·매년은 시작월부터 셉니다. 주말·공휴일은 옮기지 않으니 만든 뒤 출납일보에서 날짜를 고쳐 주세요.
        자동이체를 해지하면 규칙을 지우기보다 <b>종료월</b>을 넣어 두시면 지난 기록이 규칙과 함께 남습니다.
      </p>
      <Fields f={f} setF={setF} corps={corps} withPlan={false} />
      {/* #447 — 적요는 summaryBase 달 것으로 적고, 그 뒤 달은 그 달에 맞춰 바뀐다(서버 planRules 와 같은 식). */}
      <p className="text-xs text-ink-faint">
        이 적요는 <b>{monthLabel(summaryBase)} 출납</b>에 적은 그대로 들어가고, 그 뒤로는 적요의 「11월」·「2026년 11월」·「10/17」 같은 달이 그 달에 맞춰 바뀝니다.
        {f.summary && shiftRuleSummary(f.summary, summaryBase, nextMonth(summaryBase)) !== f.summary && (
          <> 예: {monthLabel(nextMonth(summaryBase))} 출납엔 「{shiftRuleSummary(f.summary, summaryBase, nextMonth(summaryBase))}」</>
        )}
      </p>
      {error && <ErrorBox message={error} />}
      <div className="flex gap-2">
        <button className="btn-primary" onClick={() => save()} disabled={busy}>{busy ? '저장 중…' : '저장'}</button>
        <button className="btn-ghost" onClick={onClose}>닫기</button>
        {row && (confirmDel ? (
          <span className="ml-auto flex gap-1">
            <button className="btn-ghost text-red-600" onClick={() => save(true)} disabled={busy}>정말 지우기</button>
            <button className="btn-ghost" onClick={() => setConfirmDel(false)}>아니오</button>
          </span>
        ) : <button className="btn-ghost ml-auto text-red-600" onClick={() => setConfirmDel(true)}>규칙 지우기</button>)}
      </div>
    </div>
  );
}
